import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../AuthContext';
import NavBar from '../components/NavBar';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatBytes(b) {
  if (!b || b === 0) return '0 B';
  const k = 1024, sizes = ['B','KB','MB','GB','TB'];
  const i = Math.floor(Math.log(Math.max(b,1))/Math.log(k));
  return parseFloat((b/Math.pow(k,i)).toFixed(1)) + ' ' + sizes[i];
}
function formatUptime(s) {
  const d=Math.floor(s/86400),h=Math.floor((s%86400)/3600),m=Math.floor((s%3600)/60);
  if(d>0) return `${d}d ${h}h ${m}m`;
  if(h>0) return `${h}h ${m}m`;
  return `${m}m`;
}
function formatSecs(s) {
  if(!s) return '0m';
  const h=Math.floor(s/3600),m=Math.floor((s%3600)/60);
  if(h>0) return `${h}h ${m}m`;
  return `${m}m`;
}

const ACTION_META = {
  'user.login':          { icon:'🔑', color:'#00e676', label:'Login' },
  'user.logout':         { icon:'🚪', color:'#5c6b82', label:'Logout' },
  'user.created':        { icon:'👤', color:'#00e676', label:'User Created' },
  'user.deleted':        { icon:'🗑',  color:'#ff4d4d', label:'User Deleted' },
  'user.updated':        { icon:'✏️',  color:'#f59e0b', label:'User Updated' },
  'stream.start':        { icon:'🔴', color:'#00e676', label:'Stream Started' },
  'stream.end':          { icon:'⏹',  color:'#5c6b82', label:'Stream Ended' },
  'destination.added':   { icon:'➕', color:'#9146ff', label:'Destination Added' },
  'destination.deleted': { icon:'➖', color:'#ff4d4d', label:'Destination Removed' },
  'destination.failed':  { icon:'⚠️', color:'#ff4d4d', label:'Destination Failed' },
  'recording.completed': { icon:'🎬', color:'#00e676', label:'Recording Saved' },
  'recording.deleted':   { icon:'🗑',  color:'#ff4d4d', label:'Recording Deleted' },
  'settings.changed':    { icon:'⚙️', color:'#f59e0b', label:'Settings Changed' },
  'apikey.created':      { icon:'🔐', color:'#9146ff', label:'API Key Created' },
  'apikey.deleted':      { icon:'🗑',  color:'#ff4d4d', label:'API Key Deleted' },
};

const scopeColors = { read:'#00e676', stream:'#f59e0b', admin:'#ff4d4d' };

export default function Settings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === 'admin';
  const isManager = ['admin','manager'].includes(user?.role);

  // Determine initial tab from URL hash
  const initialTab = location.hash.replace('#','') || 'system';
  const [activeTab, setActiveTab] = useState(initialTab);

  const setTab = (tab) => {
    setActiveTab(tab);
    window.history.replaceState(null, '', `#${tab}`);
  };

  // ── Settings state ──
  const [settings, setSettings] = useState({});
  const [system, setSystem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  // ── VOD Library ──
  const [vodItems, setVodItems] = useState([]);
  const [radioStatus, setRadioStatus] = useState({enabled:false,radioUrl:'',muteVideo:true,videos:[],active:false});
  const [radioUploading, setRadioUploading] = useState(false);
  const [vodPlaylist, setVodPlaylist] = useState([]);
  const [fallbackStatus, setFallbackStatus] = useState({enabled:false,active:false});
  const [vodUploading, setVodUploading] = useState(false);
  const [showSourceModal, setShowSourceModal] = useState(false);
  const [sourceForm, setSourceForm] = useState({name:'',source_url:'',source_type:'hls'});
  const [sourceError, setSourceError] = useState('');
  const [importRecordings, setImportRecordings] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [vodUploadProgress, setVodUploadProgress] = useState('');

  const [abrSettings, setAbrSettings] = useState({cdn_enabled:'false',cdn_provider:'none',cdn_endpoint:'',cdn_bucket:'',cdn_access_key:'',cdn_secret_key:'',cdn_region:'auto',cdn_public_url:'',abr_enabled:'true',webrtc_enabled:'true'});
  const [abrSaving, setAbrSaving] = useState(false);
  const [abrSaved, setAbrSaved] = useState(false);
  const [testingCdn, setTestingCdn] = useState(false);
  const [cdnTestResult, setCdnTestResult] = useState(null);
  const [renewingSSL, setRenewingSSL] = useState(false);
  const [sslRenewResult, setSslRenewResult] = useState(null);
  const [testEmailTo, setTestEmailTo] = useState(user?.email||'');
  const [testResult, setTestResult] = useState(null);
  const [pwForm, setPwForm] = useState({currentPassword:'',newPassword:'',confirm:''});
  const [logoPreview, setLogoPreview] = useState('');
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoSuccess, setLogoSuccess] = useState('');
  const [twoFa, setTwoFa] = useState({ enabled: false, qrCode: null, secret: null, code: '', step: 'idle', error: '', success: '' });
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');

  // ── IP Whitelist ──
  const [ipList, setIpList] = useState([]);
  const [newIp, setNewIp] = useState({ip_cidr:'',label:'',block_ingest:true,block_dashboard:false});

  // ── Ingest Servers ──
  const [servers, setServers] = useState([]);
  const [serverModal, setServerModal] = useState(null);
  const [serverForm, setServerForm] = useState({name:'',host:'',rtmp_port:1935,srt_port:10080,region:''});
  const [checkingServer, setCheckingServer] = useState(null);

  // ── Backup Streams ──
  const [backupStreams, setBackupStreams] = useState([]);
  const [backupModal, setBackupModal] = useState(null);
  const [backupForm, setBackupForm] = useState({name:'',type:'rtmp',source:'',trigger_on_drop:true,trigger_delay_secs:10});

  // ── Storage ──
  const [storage, setStorage] = useState(null);
  const [cleanupDays, setCleanupDays] = useState(30);
  const [gdriveSettings, setGdriveSettings] = useState({gdrive_enabled:'false',gdrive_auto_upload:'false',gdrive_folder_id:'',gdrive_client_id:'',gdrive_client_secret:'',gdrive_refresh_token:''});
  const [gdriveCredFile, setGdriveCredFile] = useState(null);
  const [gdriveTesting, setGdriveTesting] = useState(false);
  const [gdriveTestResult, setGdriveTestResult] = useState(null);
  const [gdriveUploading, setGdriveUploading] = useState(false);
  const [gdriveFiles, setGdriveFiles] = useState([]);
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [cleaning, setCleaning] = useState(false);

  // ── API Keys ──
  const [apiKeys, setApiKeys] = useState([]);
  const [keyModal, setKeyModal] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [keyForm, setKeyForm] = useState({name:'',scopes:['read'],rate_limit_per_min:60,expires_at:''});
  const [keyCopied, setKeyCopied] = useState(false);

  // ── Usage ──
  const [usage, setUsage] = useState(null);
  const [usageDays, setUsageDays] = useState(30);

  // ── Activity ──
  const [logs, setLogs] = useState([]);
  const [logTotal, setLogTotal] = useState(0);
  const [logFilter, setLogFilter] = useState('');
  const [logOffset, setLogOffset] = useState(0);

  const [error, setError] = useState('');

  const set = (k,v) => setSettings(s=>({...s,[k]:String(v)}));

  const loadSettings  = useCallback(async()=>{ try{ const {data}=await api.get('/settings'); setSettings(data); }catch{} },[]);
  const loadSystem    = useCallback(async()=>{ try{ const {data}=await api.get('/settings/system'); setSystem(data); }catch{} },[]);
  // Load current logo
  useEffect(()=>{
    api.get('/settings/logo', {responseType:'blob'}).then(r=>{
      const url = URL.createObjectURL(r.data);
      setLogoPreview(url);
    }).catch(()=>{});
  },[]);

  const handleLogoUpload = async(e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 500000) { alert('Image must be under 500KB'); return; }
    const reader = new FileReader();
    reader.onload = async(ev) => {
      const base64 = ev.target.result;
      setLogoUploading(true);
      try {
        await api.post('/settings/logo', { logo: base64 });
        setLogoPreview(base64);
        setLogoSuccess('Logo updated!');
        setTimeout(()=>setLogoSuccess(''),3000);
      } catch(e) { alert('Upload failed'); }
      setLogoUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const load2faStatus = useCallback(async()=>{
    try {
      const {data} = await api.get('/auth/me');
      setTwoFa(t=>({...t, enabled: data.totp_enabled || false}));
    } catch {}
  },[]);

  const loadIpList    = useCallback(async()=>{ if(!isAdmin) return; try{ const {data}=await api.get('/settings/ip-whitelist'); setIpList(data); }catch{} },[isAdmin]);
  const loadServers   = useCallback(async()=>{ try{ const {data}=await api.get('/ingest-servers'); setServers(data); }catch{} },[]);
  const loadBackup    = useCallback(async()=>{ try{ const {data}=await api.get('/backup-streams'); setBackupStreams(data); }catch{} },[]);
  const loadStorage   = useCallback(async()=>{ if(!isAdmin) return; try{ const {data}=await api.get('/storage'); setStorage(data); }catch{} },[isAdmin]);
  const loadVod = useCallback(async()=>{ try{ const [{data:v},{data:p},{data:f}]=await Promise.all([api.get('/vod'),api.get('/vod/playlist'),api.get('/vod/fallback/status')]); setVodItems(v); setVodPlaylist(p); setFallbackStatus(f); }catch{} },[]);
  const loadAbrSettings = useCallback(async()=>{ try{ const {data}=await api.get('/abr/settings'); setAbrSettings(data); }catch{} },[]);
  const loadApiKeys   = useCallback(async()=>{ try{ const {data}=await api.get('/api/keys'); setApiKeys(data); }catch{} },[]);
  const loadUsage     = useCallback(async()=>{ if(!isManager) return; try{ const {data}=await api.get(`/usage?days=${usageDays}`); setUsage(data); }catch{} },[isManager,usageDays]);
  const loadLogs      = useCallback(async()=>{
    if(!isManager) return;
    try{
      const params=new URLSearchParams({limit:50,offset:logOffset});
      if(logFilter) params.set('action',logFilter);
      const {data}=await api.get(`/activity?${params}`);
      setLogs(data.logs||[]); setLogTotal(data.total||0);
    }catch{}
  },[isManager,logFilter,logOffset]);

  useEffect(()=>{ loadSettings(); loadSystem(); const t=setInterval(loadSystem,30000); return()=>clearInterval(t); },[loadSettings,loadSystem]);
  useEffect(()=>{ if(activeTab==='ip-whitelist') loadIpList(); },[activeTab,loadIpList]);
  useEffect(()=>{ if(activeTab==='servers') loadServers(); },[activeTab,loadServers]);
  useEffect(()=>{ 
    if(activeTab==='storage') { 
      loadStorage();
      api.get('/settings').then(({data})=>{
        setGdriveSettings({
          gdrive_enabled: data.gdrive_enabled||'false',
          gdrive_auto_upload: data.gdrive_auto_upload||'false',
          gdrive_folder_id: data.gdrive_folder_id||'',
          gdrive_client_id: data.gdrive_client_id||'',
          gdrive_client_secret: data.gdrive_client_secret ? '••••••••' : '',
          gdrive_refresh_token: data.gdrive_refresh_token ? '••••••••' : ''
        });
      }).catch(()=>{});
    }
  },[activeTab,loadStorage]);
  useEffect(()=>{ if(activeTab==='backup') loadBackup(); },[activeTab,loadBackup]);
  useEffect(()=>{ if(activeTab==='api-keys') loadApiKeys(); },[activeTab,loadApiKeys]);
  useEffect(()=>{ if(activeTab==='cdn') loadAbrSettings(); },[activeTab,loadAbrSettings]);
  useEffect(()=>{ 
    if(activeTab==='vod') { 
      loadVod(); 
      api.get('/vod/radio-status').then(({data})=>setRadioStatus(data)).catch(()=>{});
    }
  },[activeTab,loadVod]);
  useEffect(()=>{ if(activeTab==='reports') loadUsage(); },[activeTab,loadUsage,usageDays]);
  useEffect(()=>{ if(activeTab==='activity') loadLogs(); },[activeTab,loadLogs,logFilter,logOffset]);

  const handleSave = async()=>{
    setSaving(true);
    try{ const {data}=await api.patch('/settings',settings); if(data.saved) setSettings(s=>({...s,...data.saved})); setSaved(true); setTimeout(()=>setSaved(false),2000); }
    catch(e){ console.error(e); } finally{ setSaving(false); }
  };

  const handleRenewSSL = async()=>{
    setRenewingSSL(true); setSslRenewResult(null);
    try{
      const {data}=await api.post('/settings/renew-ssl');
      setSslRenewResult({success:data.success, message:data.output||data.error});
    }catch(e){
      setSslRenewResult({success:false, message:e.response?.data?.error||'Failed'});
    }finally{ setRenewingSSL(false); loadSystem(); }
  };

  const handleTestEmail = async()=>{
    setTestingEmail(true); setTestResult(null);
    try{ const {data}=await api.post('/settings/test-email',{to:testEmailTo}); setTestResult(data); }
    catch(e){ setTestResult({success:false,error:e.response?.data?.error||'Failed'}); }
    finally{ setTestingEmail(false); }
  };

  const handleChangePassword = async()=>{
    setPwError(''); setPwSuccess('');
    if(!pwForm.currentPassword||!pwForm.newPassword){setPwError('All fields required');return;}
    if(pwForm.newPassword!==pwForm.confirm){setPwError('Passwords do not match');return;}
    if(pwForm.newPassword.length<8){setPwError('Minimum 8 characters');return;}
    try{
      await api.post('/auth/change-password',{currentPassword:pwForm.currentPassword,newPassword:pwForm.newPassword});
      setPwSuccess('Password changed successfully');
      setPwForm({currentPassword:'',newPassword:'',confirm:''});
    }catch(e){ setPwError(e.response?.data?.error||'Failed'); }
  };

  // IP Whitelist handlers
  const addIp = async()=>{
    if(!newIp.ip_cidr) return;
    try{ await api.post('/settings/ip-whitelist',newIp); setNewIp({ip_cidr:'',label:'',block_ingest:true,block_dashboard:false}); loadIpList(); }
    catch(e){ alert(e.response?.data?.error||'Failed'); }
  };
  const removeIp = async(id)=>{ await api.delete(`/settings/ip-whitelist/${id}`); loadIpList(); };

  // Server handlers
  const saveServer = async()=>{
    if(!serverForm.name||!serverForm.host){setError('Name and host required');return;}
    setError('');
    try{
      if(serverModal==='create') await api.post('/ingest-servers',serverForm);
      else await api.patch(`/ingest-servers/${serverModal.id}`,serverForm);
      setServerModal(null); loadServers();
    }catch(e){ setError(e.response?.data?.error||'Failed'); }
  };
  const checkServer = async(id)=>{ setCheckingServer(id); try{ await api.post(`/ingest-servers/${id}/check`); loadServers(); }catch{} finally{ setCheckingServer(null); }};
  const checkAllServers = async()=>{ setCheckingServer('all'); try{ await api.post('/ingest-servers/check-all'); loadServers(); }catch{} finally{ setCheckingServer(null); }};

  // Storage handlers
  const previewCleanup = async()=>{
    try{ const {data}=await api.post('/storage/cleanup',{dry_run:true,max_age_days:cleanupDays}); setCleanupPreview(data); }catch{}
  };
  const runCleanup = async()=>{
    if(!window.confirm(`Delete ${cleanupPreview?.would_delete} recordings older than ${cleanupDays} days?`)) return;
    setCleaning(true);
    try{ await api.post('/storage/cleanup',{dry_run:false,max_age_days:cleanupDays}); setCleanupPreview(null); loadStorage(); }
    catch{} finally{ setCleaning(false); }
  };

  // API Key handlers
  const createKey = async()=>{
    if(!keyForm.name){setError('Name required');return;}
    setError('');
    try{ const {data}=await api.post('/api/keys',keyForm); setNewKey(data); setKeyModal(false); setKeyForm({name:'',scopes:['read'],rate_limit_per_min:60,expires_at:''}); loadApiKeys(); }
    catch(e){ setError(e.response?.data?.error||'Failed'); }
  };
  const toggleKey = async(key)=>{ await api.patch(`/api/keys/${key.id}`,{enabled:!key.enabled}); loadApiKeys(); };
  const deleteKey = async(key)=>{ if(!window.confirm(`Delete key "${key.name}"?`)) return; await api.delete(`/api/keys/${key.id}`); loadApiKeys(); };
  const toggleScope = (scope)=>setKeyForm(f=>({...f,scopes:f.scopes.includes(scope)?f.scopes.filter(s=>s!==scope):[...f.scopes,scope]}));

  const sslColor = system?.ssl?.daysLeft>30?'var(--signal)':system?.ssl?.daysLeft>7?'#f59e0b':'var(--danger)';
  const maxStreamSecs = usage?Math.max(...(usage.userUsage||[]).map(u=>u.total_stream_secs||0),1):1;

  // ─── Styles ───────────────────────────────────────────────────────────────
  const s = {
    page: {minHeight:'100vh',background:'var(--bg)'},
    main: {maxWidth:'960px',margin:'0 auto',padding:'32px 24px'},
    pageTitle: {fontSize:'24px',fontWeight:'800',letterSpacing:'-0.5px',marginBottom:'24px'},
    tabs: {display:'flex',gap:'2px',marginBottom:'28px',background:'var(--surface)',padding:'4px',borderRadius:'10px',border:'1px solid var(--border)',flexWrap:'wrap'},
    tab: (a)=>({padding:'6px 14px',borderRadius:'7px',fontSize:'12px',fontWeight:'600',cursor:'pointer',background:a?'var(--surface2)':'transparent',color:a?'var(--text)':'var(--text-muted)',border:'none',whiteSpace:'nowrap'}),
    card: {background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',padding:'20px 24px',marginBottom:'16px'},
    cardTitle: {fontSize:'14px',fontWeight:'700',marginBottom:'16px',display:'flex',alignItems:'center',justifyContent:'space-between'},
    field: {marginBottom:'14px'},
    label: {display:'block',fontSize:'11px',fontWeight:'700',color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.8px',marginBottom:'6px'},
    hint: {fontSize:'12px',color:'var(--text-muted)',marginTop:'4px'},
    grid2: {display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'},
    statGrid: {display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'12px',marginBottom:'16px'},
    statCard: (color)=>({background:'var(--surface2)',border:`1px solid ${color||'var(--border)'}`,borderRadius:'10px',padding:'14px 16px'}),
    statLabel: {fontSize:'11px',fontWeight:'700',color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.8px',marginBottom:'5px'},
    statValue: (color)=>({fontSize:'20px',fontWeight:'800',color:color||'var(--text)',letterSpacing:'-0.5px'}),
    statSub: {fontSize:'12px',color:'var(--text-muted)',marginTop:'2px'},
    barWrap: {height:'5px',background:'var(--surface)',borderRadius:'3px',overflow:'hidden',marginTop:'6px'},
    bar: (pct,color)=>({height:'100%',width:`${Math.min(pct,100)}%`,background:color||'var(--signal)',borderRadius:'3px',transition:'width 0.3s'}),
    saveRow: {display:'flex',gap:'10px',marginTop:'18px'},
    saveBtn: {padding:'9px 22px',background:'var(--signal)',color:'#000',fontWeight:'700',fontSize:'13px',borderRadius:'var(--radius)',border:'none',cursor:'pointer'},
    savedBadge: {padding:'9px 14px',background:'rgba(0,230,118,0.1)',color:'var(--signal)',borderRadius:'var(--radius)',fontSize:'13px',fontWeight:'600',border:'1px solid rgba(0,230,118,0.2)'},
    toggle: (on)=>({display:'inline-flex',alignItems:'center',gap:'8px',cursor:'pointer',fontSize:'13px',color:on?'var(--signal)':'var(--text-muted)'}),
    toggleSwitch: (on)=>({width:'34px',height:'19px',borderRadius:'10px',background:on?'var(--signal)':'var(--border)',position:'relative',flexShrink:0,transition:'background 0.2s'}),
    toggleKnob: (on)=>({position:'absolute',top:'3px',left:on?'18px':'3px',width:'13px',height:'13px',borderRadius:'50%',background:'#fff',transition:'left 0.2s'}),
    errorBox: {background:'var(--danger-bg)',border:'1px solid rgba(255,77,77,0.25)',color:'var(--danger)',borderRadius:'var(--radius)',padding:'10px 14px',fontSize:'13px',marginBottom:'14px'},
    successBox: {background:'rgba(0,230,118,0.07)',border:'1px solid rgba(0,230,118,0.2)',color:'var(--signal)',borderRadius:'var(--radius)',padding:'10px 14px',fontSize:'13px',marginBottom:'14px'},
    addBtn: {background:'var(--signal)',color:'#000',fontWeight:'700',fontSize:'12px',borderRadius:'7px',padding:'6px 14px',cursor:'pointer',border:'none'},
    table: {width:'100%',borderCollapse:'collapse'},
    th: {textAlign:'left',padding:'8px 12px',fontSize:'11px',fontWeight:'700',color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.8px',borderBottom:'1px solid var(--border)',background:'var(--surface2)'},
    td: {padding:'11px 12px',fontSize:'13px',borderBottom:'1px solid var(--border)',verticalAlign:'middle'},
    badge: (on)=>({fontSize:'10px',fontWeight:'700',padding:'2px 6px',borderRadius:'4px',background:on?'rgba(0,230,118,0.1)':'var(--surface2)',color:on?'var(--signal)':'var(--text-muted)',border:`1px solid ${on?'rgba(0,230,118,0.2)':'var(--border)'}`}),
    delBtn: {background:'var(--danger-bg)',border:'1px solid rgba(255,77,77,0.2)',color:'var(--danger)',borderRadius:'6px',padding:'4px 10px',fontSize:'12px',cursor:'pointer'},
    editBtn: {background:'var(--surface2)',border:'1px solid var(--border)',color:'var(--text)',borderRadius:'6px',padding:'4px 10px',fontSize:'12px',cursor:'pointer'},
    mono: {fontFamily:'var(--mono)',fontSize:'11px',color:'var(--text-muted)'},
    // Status dot
    statusDot: (st)=>({width:'8px',height:'8px',borderRadius:'50%',background:st==='online'?'var(--signal)':st==='offline'?'var(--danger)':'var(--text-muted)',boxShadow:st==='online'?'0 0 5px var(--signal)':'none',flexShrink:0}),
    // Containers
    containerRow: {display:'flex',alignItems:'center',gap:'10px',padding:'8px 12px',background:'var(--surface2)',borderRadius:'7px',marginBottom:'5px'},
    cDot: (up)=>({width:'7px',height:'7px',borderRadius:'50%',background:up?'var(--signal)':up===null?'var(--text-muted)':'var(--danger)',flexShrink:0}),
    // Log rows
    logRow: {display:'flex',alignItems:'flex-start',gap:'10px',padding:'9px 14px',background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'8px',marginBottom:'4px'},
    logIcon: (color)=>({width:'26px',height:'26px',borderRadius:'6px',background:`${color}18`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'13px',flexShrink:0}),
    // Scope badges
    scopeBadge: (sc)=>({fontSize:'10px',fontWeight:'700',padding:'2px 6px',borderRadius:'4px',background:`${scopeColors[sc]||'#5c6b82'}15`,color:scopeColors[sc]||'#5c6b82',border:`1px solid ${scopeColors[sc]||'#5c6b82'}30`}),
    // Modal
    overlay: {position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:100,padding:'24px'},
    modal: {background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',padding:'32px',width:'100%',maxWidth:'460px',maxHeight:'90vh',overflowY:'auto'},
    modalTitle: {fontSize:'19px',fontWeight:'800',marginBottom:'22px'},
    modalSave: {width:'100%',padding:'11px',background:'var(--signal)',color:'#000',fontWeight:'700',fontSize:'14px',borderRadius:'var(--radius)',border:'none',cursor:'pointer',marginTop:'8px'},
    modalCancel: {width:'100%',padding:'10px',background:'var(--surface2)',color:'var(--text)',fontWeight:'600',fontSize:'14px',borderRadius:'var(--radius)',border:'1px solid var(--border)',cursor:'pointer',marginTop:'8px'},
  };

  const Toggle = ({value,onChange,label})=>(
    <div style={s.toggle(value)} onClick={()=>onChange(!value)}>
      <div style={s.toggleSwitch(value)}><div style={s.toggleKnob(value)}/></div>
      {label}
    </div>
  );

  const tabs = [
    {id:'system',label:'⚙ System'},
    {id:'station',label:'📻 Station'},
    ...(isAdmin?[
      {id:'smtp',label:'📧 Email'},
      {id:'ip-whitelist',label:'🔒 IP Whitelist'},
      {id:'servers',label:'🖥 Ingest Servers'},
      {id:'storage',label:'💾 Storage'},
      {id:'backup',label:'📡 Backup Stream'},
    ]:[]),
    {id:'api-keys',label:'🔑 API Keys'},
    {id:'cdn',label:'🌐 CDN & ABR'},
    {id:'vod',label:'📼 VOD & Fallback'},
    ...(isManager?[
      {id:'reports',label:'📊 Reports'},
      {id:'activity',label:'📋 Activity'},
    ]:[]),
    {id:'account',label:'👤 Account'},
  ];

  return (
        <NavBar>
    <div style={s.page}>
      <div style={s.main}>
        <div style={s.pageTitle}>Settings</div>
        <div style={s.tabs}>
          {tabs.map(t=><button key={t.id} style={s.tab(activeTab===t.id)} onClick={()=>setTab(t.id)}>{t.label}</button>)}
        </div>

        {/* ── SYSTEM ── */}
        {activeTab==='system'&&(
          <>
            <div style={s.statGrid}>
              <div style={s.statCard()}>
                <div style={s.statLabel}>Server Uptime</div>
                <div style={s.statValue()}>{system?formatUptime(system.uptime):'…'}</div>
                <div style={s.statSub}>{system?.cpu?.cores?`${system.cpu.cores} cores`:''}</div>
              </div>
              <div style={s.statCard(sslColor)} onClick={isAdmin?()=>setSslRenewResult(null):undefined}>
                <div style={s.statLabel}>SSL Certificate</div>
                <div style={s.statValue(sslColor)}>{system?.ssl?.daysLeft!=null?`${system.ssl.daysLeft}d`:'…'}</div>
                <div style={s.statSub}>{system?.ssl?.expiry?`Expires ${new Date(system.ssl.expiry).toLocaleDateString()}`:'Checking…'}</div>
                {isAdmin&&(
                  <button
                    style={{marginTop:'8px',padding:'4px 10px',background:'transparent',border:`1px solid ${sslColor}`,color:sslColor,borderRadius:'6px',fontSize:'11px',fontWeight:'700',cursor:'pointer',width:'100%'}}
                    onClick={e=>{e.stopPropagation();handleRenewSSL();}}
                    disabled={renewingSSL}
                  >
                    {renewingSSL?'Renewing…':'↻ Renew Now'}
                  </button>
                )}
              </div>
              <div style={s.statCard()}>
                <div style={s.statLabel}>Recordings</div>
                <div style={s.statValue()}>{system?system.recordings.count:'…'}</div>
                <div style={s.statSub}>{system?formatBytes(system.recordings.totalBytes):''}</div>
              </div>
            </div>
            {sslRenewResult&&(
              <div style={{...sslRenewResult.success?s.successBox:s.errorBox,marginBottom:'16px'}}>
                {sslRenewResult.success?'✓':'✕'} {sslRenewResult.message}
              </div>
            )}
            <div style={s.card}>
              <div style={s.cardTitle}>CPU</div>
              {system&&<><div style={{display:'flex',justifyContent:'space-between',fontSize:'13px',color:'var(--text-muted)',marginBottom:'5px'}}><span>{system.cpu?.percent!=null?`${system.cpu.percent}% used`:'Measuring…'}</span><span>{system.cpu?.cores} cores</span></div><div style={s.barWrap}><div style={s.bar(system.cpu?.percent||0,(system.cpu?.percent||0)>90?'var(--danger)':(system.cpu?.percent||0)>70?'#f59e0b':'var(--signal)')}/></div></>}
            </div>
            <div style={s.card}>
              <div style={s.cardTitle}>Memory</div>
              {system&&<><div style={{display:'flex',justifyContent:'space-between',fontSize:'13px',color:'var(--text-muted)',marginBottom:'5px'}}><span>Used: {formatBytes(system.memory.used)}</span><span>Total: {formatBytes(system.memory.total)}</span></div><div style={s.barWrap}><div style={s.bar(system.memory.used/system.memory.total*100,system.memory.used/system.memory.total>0.85?'var(--danger)':'var(--signal)')}/></div></>}
            </div>
            <div style={s.card}>
              <div style={s.cardTitle}>Disk</div>
              {system&&<><div style={{display:'flex',justifyContent:'space-between',fontSize:'13px',color:'var(--text-muted)',marginBottom:'5px'}}><span>Used: {formatBytes(system.disk.used)}</span><span>Free: {formatBytes(system.disk.free)}</span></div><div style={s.barWrap}><div style={s.bar(system.disk.used/system.disk.total*100,system.disk.used/system.disk.total>0.9?'var(--danger)':system.disk.used/system.disk.total>0.75?'#f59e0b':'var(--signal)')}/></div></>}
            </div>
            <div style={s.card}>
              <div style={s.cardTitle}>Containers</div>
              {(system?.containers||[]).map((c,i)=>(
                <div key={i} style={s.containerRow}>
                  <div style={s.cDot(c.up)}/>
                  <div style={{flex:1,fontWeight:'600',fontSize:'13px'}}>{c.name}</div>
                  <div style={{fontSize:'12px',color:'var(--text-muted)'}}>{c.status}{c.note?` (${c.note})`:''}</div>
                </div>
              ))}
            </div>
            {system?.activeSessions?.length>0&&(
              <div style={s.card}>
                <div style={s.cardTitle}>🔴 Live Now</div>
                {system.activeSessions.map((sess,i)=>(
                  <div key={i} style={{...s.containerRow,marginBottom:'6px'}}>
                    <div style={s.cDot(true)}/>
                    <div style={{flex:1,fontWeight:'600',fontSize:'13px'}}>{sess.name}</div>
                    {sess.recording_enabled&&<span style={s.badge(true)}>REC</span>}
                    <div style={{fontSize:'12px',color:'var(--text-muted)'}}>Since {new Date(sess.started_at).toLocaleTimeString()}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── STATION ── */}
        {activeTab==='station'&&(
          <div style={s.card}>
            <div style={s.cardTitle}>Station Configuration</div>
            {isAdmin && (
              <div style={s.field}>
                <label style={s.label}>Station Logo</label>
                <div style={{display:'flex',alignItems:'center',gap:'14px',flexWrap:'wrap'}}>
                  {logoPreview && <img src={logoPreview} alt="logo" style={{width:'60px',height:'60px',borderRadius:'10px',objectFit:'contain',background:'var(--surface2)',border:'1px solid var(--border)',padding:'4px'}}/>}
                  <div>
                    <label style={{display:'inline-block',padding:'8px 16px',background:'var(--signal)',color:'#fff',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontWeight:'600'}}>
                      {logoUploading ? 'Uploading…' : '📁 Choose Image'}
                      <input type="file" accept="image/*" onChange={handleLogoUpload} style={{display:'none'}} disabled={logoUploading}/>
                    </label>
                    <div style={{fontSize:'11px',color:'var(--text-muted)',marginTop:'5px'}}>PNG, JPG, SVG — max 500KB. Used in sidebar and login page.</div>
                    {logoSuccess && <div style={{color:'var(--signal)',fontSize:'12px',marginTop:'4px'}}>✓ {logoSuccess}</div>}
                  </div>
                </div>
              </div>
            )}
            <div style={s.field}><label style={s.label}>Station Name</label><input value={settings.station_name||''} onChange={e=>set('station_name',e.target.value)} placeholder="Dennery FM 88.9" disabled={!isAdmin}/></div>
            <div style={s.field}><label style={s.label}>Tagline</label><input value={settings.station_tagline||''} onChange={e=>set('station_tagline',e.target.value)} disabled={!isAdmin}/></div>
            <div style={s.field}><label style={s.label}>Public Page Title</label><input value={settings.public_page_title||''} onChange={e=>set('public_page_title',e.target.value)} disabled={!isAdmin}/></div>
            {isAdmin&&<>
              <div style={s.field}><Toggle value={settings.public_page_enabled==='true'} onChange={v=>set('public_page_enabled',v)} label="Public live page enabled (/live)"/></div>
              <div style={s.field}><Toggle value={settings.recording_enabled_default==='true'} onChange={v=>set('recording_enabled_default',v)} label="Recording on by default"/></div>
              <div style={s.field}><Toggle value={settings.auto_restart_enabled==='true'} onChange={v=>set('auto_restart_enabled',v)} label="Auto-restart failed destinations"/></div>
              <div style={s.field}><label style={s.label}>Max restart attempts</label><input type="number" min="1" max="20" value={settings.auto_restart_max_retries||'5'} onChange={e=>set('auto_restart_max_retries',e.target.value)} style={{width:'100px'}}/></div>
              <div style={s.saveRow}>
                <button style={s.saveBtn} onClick={handleSave} disabled={saving}>{saving?'Saving…':'Save changes'}</button>
                {saved&&<div style={s.savedBadge}>✓ Saved</div>}
              </div>
            </>}
          </div>
        )}

        {/* ── SMTP ── */}
        {activeTab==='smtp'&&isAdmin&&(
          <>
            <div style={s.card}>
              <div style={s.cardTitle}>SMTP Configuration</div>
              <div style={s.field}><Toggle value={settings.smtp_enabled==='true'} onChange={v=>set('smtp_enabled',v)} label="Email alerts enabled"/></div>
              <div style={s.grid2}>
                <div style={s.field}><label style={s.label}>SMTP Host</label><input value={settings.smtp_host||''} onChange={e=>set('smtp_host',e.target.value)} placeholder="smtp.gmail.com"/></div>
                <div style={s.field}><label style={s.label}>Port</label><input value={settings.smtp_port||'587'} onChange={e=>set('smtp_port',e.target.value)}/></div>
              </div>
              <div style={s.grid2}>
                <div style={s.field}><label style={s.label}>Username</label><input value={settings.smtp_user||''} onChange={e=>set('smtp_user',e.target.value)}/></div>
                <div style={s.field}><label style={s.label}>Password</label><input type="password" value={settings.smtp_pass||''} onChange={e=>set('smtp_pass',e.target.value)} placeholder="App password"/><div style={s.hint}>For Gmail use an App Password</div></div>
              </div>
              <div style={s.field}><label style={s.label}>From Address</label><input value={settings.smtp_from||''} onChange={e=>set('smtp_from',e.target.value)}/></div>
              <div style={s.field}><label style={s.label}>Alert Recipient</label><input value={settings.alert_email||''} onChange={e=>set('alert_email',e.target.value)}/></div>
              <div style={s.label} style={{marginBottom:'8px'}}>Alert triggers</div>
              <div style={{display:'flex',gap:'12px',flexWrap:'wrap',marginBottom:'4px'}}>
                <Toggle value={settings.alert_on_stream_drop==='true'} onChange={v=>set('alert_on_stream_drop',v)} label="Stream dropped"/>
                <Toggle value={settings.alert_on_dest_fail==='true'} onChange={v=>set('alert_on_dest_fail',v)} label="Destination failed"/>
                <Toggle value={settings.alert_on_stream_start==='true'} onChange={v=>set('alert_on_stream_start',v)} label="Stream started"/>
                <Toggle value={settings.alert_on_disk_warn==='true'} onChange={v=>set('alert_on_disk_warn',v)} label="Disk space warning (80%)"/>
              </div>
              <div style={s.saveRow}><button style={s.saveBtn} onClick={handleSave} disabled={saving}>{saving?'Saving…':'Save'}</button>{saved&&<div style={s.savedBadge}>✓ Saved</div>}</div>
            </div>
            <div style={s.card}>
              <div style={s.cardTitle}>Test Email</div>
              <div style={{display:'flex',gap:'10px',alignItems:'flex-end'}}>
                <div style={{flex:1}}><label style={s.label}>Send to</label><input value={testEmailTo} onChange={e=>setTestEmailTo(e.target.value)}/></div>
                <button style={{...s.saveBtn,padding:'10px 18px'}} onClick={handleTestEmail} disabled={testingEmail}>{testingEmail?'Sending…':'Send test'}</button>
              </div>
              {testResult&&<div style={{marginTop:'12px',...(testResult.success?s.successBox:s.errorBox)}}>{testResult.success?'✓ Sent successfully':`✕ ${testResult.error}`}</div>}
            </div>
          </>
        )}

        {/* ── IP WHITELIST ── */}
        {activeTab==='ip-whitelist'&&isAdmin&&(
          <div style={s.card}>
            <div style={s.cardTitle}>IP Whitelist</div>
            <div style={{display:'flex',gap:'20px',marginBottom:'16px',flexWrap:'wrap'}}>
              <Toggle value={settings.ip_whitelist_enabled==='true'} onChange={v=>{set('ip_whitelist_enabled',v);handleSave();}} label="Block unknown IPs from ingest"/>
              <Toggle value={settings.ip_whitelist_block_dashboard==='true'} onChange={v=>{set('ip_whitelist_block_dashboard',v);handleSave();}} label="Also block dashboard"/>
            </div>
            <div style={{background:'rgba(245,158,11,0.08)',border:'1px solid rgba(245,158,11,0.2)',borderRadius:'8px',padding:'10px 14px',fontSize:'13px',color:'#f59e0b',marginBottom:'14px'}}>
              ⚠️ Add your encoder's IP before enabling
            </div>
            {ipList.map(ip=>(
              <div key={ip.id} style={{display:'flex',alignItems:'center',gap:'10px',padding:'9px 14px',background:'var(--surface2)',borderRadius:'8px',marginBottom:'5px'}}>
                <span style={{fontFamily:'var(--mono)',fontSize:'13px',flex:1}}>{ip.ip_cidr}</span>
                <span style={{fontSize:'13px',color:'var(--text-muted)',flex:1}}>{ip.label}</span>
                <span style={s.badge(ip.block_ingest)}>Ingest</span>
                <span style={s.badge(ip.block_dashboard)}>Dashboard</span>
                <button style={s.delBtn} onClick={()=>removeIp(ip.id)}>Remove</button>
              </div>
            ))}
            <div style={{marginTop:'16px',paddingTop:'16px',borderTop:'1px solid var(--border)'}}>
              <div style={s.grid2}>
                <div style={s.field}><label style={s.label}>IP / CIDR</label><input value={newIp.ip_cidr} onChange={e=>setNewIp({...newIp,ip_cidr:e.target.value})} placeholder="192.168.1.x or 10.0.0.0/24"/></div>
                <div style={s.field}><label style={s.label}>Label</label><input value={newIp.label} onChange={e=>setNewIp({...newIp,label:e.target.value})} placeholder="Studio encoder"/></div>
              </div>
              <div style={{display:'flex',gap:'16px',marginBottom:'12px'}}>
                <Toggle value={newIp.block_ingest} onChange={v=>setNewIp({...newIp,block_ingest:v})} label="Ingest"/>
                <Toggle value={newIp.block_dashboard} onChange={v=>setNewIp({...newIp,block_dashboard:v})} label="Dashboard"/>
              </div>
              <button style={s.saveBtn} onClick={addIp}>Add IP</button>
            </div>
          </div>
        )}

        {/* ── INGEST SERVERS ── */}
        {activeTab==='servers'&&(
          <>
            <div style={{background:'rgba(0,230,118,0.05)',border:'1px solid rgba(0,230,118,0.15)',borderRadius:'10px',padding:'12px 16px',marginBottom:'16px',fontSize:'13px',color:'var(--text-muted)'}}>
              💡 Add your backup VPS as a second ingest server. DJs push to either server with the same stream key.
            </div>
            <div style={{display:'flex',justifyContent:'flex-end',gap:'8px',marginBottom:'12px'}}>
              <button style={s.editBtn} onClick={checkAllServers} disabled={checkingServer==='all'}>{checkingServer==='all'?'Checking…':'Check All'}</button>
              {isAdmin&&<button style={s.addBtn} onClick={()=>{setServerForm({name:'',host:'',rtmp_port:1935,srt_port:10080,region:''});setError('');setServerModal('create');}}>+ Add Server</button>}
            </div>
            {servers.map(server=>(
              <div key={server.id} style={{...s.card,opacity:server.enabled?1:0.6}}>
                <div style={{display:'flex',alignItems:'center',gap:'10px',marginBottom:'12px'}}>
                  <div style={s.statusDot(server.status)}/>
                  <div style={{fontWeight:'700',fontSize:'15px',flex:1}}>{server.name}</div>
                  {server.is_primary&&<span style={{fontSize:'10px',fontWeight:'800',padding:'2px 7px',borderRadius:'4px',background:'rgba(0,230,118,0.1)',color:'var(--signal)',border:'1px solid rgba(0,230,118,0.2)'}}>PRIMARY</span>}
                  {server.region&&<span style={{fontSize:'11px',color:'var(--text-muted)'}}>{server.region}</span>}
                  <span style={{fontSize:'12px',fontWeight:'600',color:server.status==='online'?'var(--signal)':server.status==='offline'?'var(--danger)':'var(--text-muted)'}}>{server.status}</span>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px',marginBottom:'12px'}}>
                  {[['RTMP',server.rtmpUrl],['RTMPS',server.rtmpsUrl]].map(([l,v])=>(
                    <div key={l} style={{background:'var(--surface2)',borderRadius:'7px',padding:'9px 12px',border:'1px solid var(--border)'}}>
                      <div style={{fontSize:'10px',fontWeight:'700',color:'var(--text-muted)',marginBottom:'3px'}}>{l}</div>
                      <div style={{fontFamily:'var(--mono)',fontSize:'11px',color:'var(--signal)',wordBreak:'break-all'}}>{v}</div>
                    </div>
                  ))}
                  <div style={{gridColumn:'1/-1',background:'var(--surface2)',borderRadius:'7px',padding:'9px 12px',border:'1px solid var(--border)'}}>
                    <div style={{fontSize:'10px',fontWeight:'700',color:'var(--text-muted)',marginBottom:'3px'}}>SRT</div>
                    <div style={{fontFamily:'var(--mono)',fontSize:'11px',color:'var(--signal)',wordBreak:'break-all'}}>{server.srtUrl}</div>
                  </div>
                </div>
                {isAdmin&&(
                  <div style={{display:'flex',gap:'6px',flexWrap:'wrap'}}>
                    <button style={s.editBtn} onClick={()=>checkServer(server.id)} disabled={checkingServer===server.id}>{checkingServer===server.id?'Checking…':'Health Check'}</button>
                    <button style={s.editBtn} onClick={()=>{setServerForm({name:server.name,host:server.host,rtmp_port:server.rtmp_port,srt_port:server.srt_port,region:server.region||''});setError('');setServerModal(server);}}>Edit</button>
                    {!server.is_primary&&<button style={s.delBtn} onClick={async()=>{if(!window.confirm('Remove server?')) return; await api.delete(`/ingest-servers/${server.id}`); loadServers();}}>Remove</button>}
                  </div>
                )}
              </div>
            ))}
          </>
        )}

        {/* ── STORAGE ── */}
        {activeTab==='storage'&&isAdmin&&(
          <>
            <div style={s.card}>
              <div style={s.cardTitle}>Disk Usage</div>
              {storage?.disk&&<>
                <div style={{display:'flex',justifyContent:'space-between',fontSize:'13px',color:'var(--text-muted)',marginBottom:'5px'}}><span>Used: {formatBytes(storage.disk.used)}</span><span>Free: {formatBytes(storage.disk.free)}</span></div>
                <div style={s.barWrap}><div style={s.bar(storage.disk.used/storage.disk.total*100)}/></div>
              </>}
            </div>
            <div style={s.card}>
              <div style={s.cardTitle}>Per User Storage</div>
              <table style={s.table}>
                <thead><tr><th style={s.th}>User</th><th style={s.th}>Recordings</th><th style={s.th}>Storage Used</th><th style={s.th}>Oldest</th></tr></thead>
                <tbody>{(storage?.userStats||[]).filter(u=>u.recording_count>0).map(u=>(
                  <tr key={u.id}>
                    <td style={s.td}><div style={{fontWeight:'600'}}>{u.name}</div><div style={{fontSize:'11px',color:'var(--text-muted)'}}>{u.email}</div></td>
                    <td style={s.td}>{u.recording_count}</td>
                    <td style={s.td}>{formatBytes(u.total_bytes)}</td>
                    <td style={s.td}>{u.oldest_recording?new Date(u.oldest_recording).toLocaleDateString():'—'}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
            <div style={s.card}>
              <div style={s.cardTitle}>Cleanup</div>
              <div style={{display:'flex',gap:'12px',alignItems:'center',marginBottom:'14px',flexWrap:'wrap'}}>
                <div><label style={s.label}>Delete recordings older than</label>
                  <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                    <input type="number" value={cleanupDays} onChange={e=>setCleanupDays(parseInt(e.target.value))} style={{width:'80px'}}/>
                    <span style={{fontSize:'13px',color:'var(--text-muted)'}}>days</span>
                  </div>
                </div>
                <div style={{display:'flex',gap:'8px',alignSelf:'flex-end'}}>
                  <button style={s.editBtn} onClick={previewCleanup}>Preview</button>
                  {cleanupPreview&&<button style={{...s.saveBtn,background:'var(--danger)',color:'#fff'}} onClick={runCleanup} disabled={cleaning}>{cleaning?'Deleting…':`Delete ${cleanupPreview.would_delete} files`}</button>}
                </div>
              </div>
              {cleanupPreview&&(
                <div style={{background:'rgba(255,77,77,0.06)',border:'1px solid rgba(255,77,77,0.2)',borderRadius:'8px',padding:'12px 16px',fontSize:'13px'}}>
                  Would delete <strong>{cleanupPreview.would_delete}</strong> recordings and free <strong>{formatBytes(cleanupPreview.would_free_bytes)}</strong>
                </div>
              )}
            </div>

            {/* Google Drive Card */}
            <div style={s.card}>
              <div style={s.cardTitle}>☁️ Google Drive Backup</div>
              <div style={{fontSize:'13px',color:'var(--text-muted)',marginBottom:'14px'}}>
                Automatically upload recordings to Google Drive after each show. Requires a Google Service Account.
              </div>
              <div style={s.field}>
                <Toggle value={gdriveSettings.gdrive_enabled==='true'} onChange={v=>{
                  const ns={...gdriveSettings,gdrive_enabled:v?'true':'false'};
                  setGdriveSettings(ns);
                  api.patch('/settings',{gdrive_enabled:v?'true':'false'}).catch(()=>{});
                }} label="Enable Google Drive backup"/>
              </div>
              {gdriveSettings.gdrive_enabled==='true'&&(
                <>
                  <div style={s.field}>
                    <Toggle value={gdriveSettings.gdrive_auto_upload==='true'} onChange={v=>{
                      const ns={...gdriveSettings,gdrive_auto_upload:v?'true':'false'};
                      setGdriveSettings(ns);
                      api.patch('/settings',{gdrive_auto_upload:v?'true':'false'}).catch(()=>{});
                    }} label="Auto-upload after each recording"/>
                  </div>
                  <div style={s.field}>
                    <label style={s.label}>Google Drive Folder ID</label>
                    <input value={gdriveSettings.gdrive_folder_id||''} 
                      onChange={e=>setGdriveSettings({...gdriveSettings,gdrive_folder_id:e.target.value})}
                      onBlur={()=>api.patch('/settings',{gdrive_folder_id:gdriveSettings.gdrive_folder_id}).catch(()=>{})}
                      placeholder="Paste folder ID from Drive URL"/>
                    <div style={s.hint}>From your Drive folder URL: drive.google.com/drive/folders/<strong>THIS_PART</strong></div>
                  </div>
                  <div style={s.field}>
                    <label style={s.label}>Google Account</label>
                    <div style={{display:'flex',gap:'8px',flexWrap:'wrap',alignItems:'center'}}>
                      <button style={{padding:'8px 14px',background:'var(--signal)',color:'#fff',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontWeight:'600',border:'none'}}
                        onClick={async()=>{
                          const {data} = await api.get('/gdrive/oauth/url').catch(e=>({data:{error:e.message}}));
                          if (data.error) { setGdriveTestResult({success:false,error:data.error}); return; }
                          window.open(data.url, '_blank', 'width=500,height=600');
                        }}>
                        🔗 Connect Google Account
                      </button>
                      <div style={{fontSize:'12px',color:'var(--text-muted)'}}>Opens Google login in a popup</div>
                    </div>
                    <div style={s.hint}>You'll be asked to sign in and grant Drive access. One-time setup.</div>
                  </div>
                  <div style={s.field}>
                    <label style={s.label}>OAuth Client ID</label>
                    <input value={gdriveSettings.gdrive_client_id||''} 
                      onChange={e=>setGdriveSettings({...gdriveSettings,gdrive_client_id:e.target.value})}
                      onBlur={()=>api.patch('/settings',{gdrive_client_id:gdriveSettings.gdrive_client_id}).catch(()=>{})}
                      placeholder="Paste from Google Cloud Console"/>
                  </div>
                  <div style={s.field}>
                    <label style={s.label}>OAuth Client Secret</label>
                    <input type="password" value={gdriveSettings.gdrive_client_secret||''} 
                      onChange={e=>setGdriveSettings({...gdriveSettings,gdrive_client_secret:e.target.value})}
                      onBlur={()=>api.patch('/settings',{gdrive_client_secret:gdriveSettings.gdrive_client_secret}).catch(()=>{})}
                      placeholder="Paste from Google Cloud Console"/>
                    <div style={s.hint}>Create OAuth 2.0 credentials at console.cloud.google.com → APIs & Services → Credentials</div>
                  </div>
                  <div style={{display:'flex',gap:'8px',flexWrap:'wrap',marginTop:'4px'}}>
                    <button style={s.editBtn} disabled={gdriveTesting} onClick={async()=>{
                      setGdriveTesting(true); setGdriveTestResult(null);
                      const {data} = await api.post('/gdrive/test').catch(e=>({data:{success:false,error:e.message}}));
                      setGdriveTestResult(data); setGdriveTesting(false);
                    }}>{gdriveTesting?'Testing…':'Test Connection'}</button>
                    <button style={s.editBtn} onClick={async()=>{
                      const {data} = await api.get('/gdrive/files').catch(()=>({data:[]}));
                      setGdriveFiles(data);
                    }}>View Drive Files</button>
                    <button style={s.editBtn} onClick={async()=>{
                      setGdriveTestResult(null);
                      const {data} = await api.post('/gdrive/test-upload').catch(e=>({data:{success:false,error:e.message}}));
                      setGdriveTestResult(data.success ? {success:true, message:`✓ Test file uploaded: ${data.file}`} : data);
                    }}>Test Upload</button>
                  </div>
                  {gdriveTestResult&&(
                    <div style={{marginTop:'10px',...(gdriveTestResult.success?s.successBox:s.errorBox)}}>
                      {gdriveTestResult.success
                        ? `✓ Connected${gdriveTestResult.folderName?' — Folder: '+gdriveTestResult.folderName:gdriveTestResult.email?' — '+gdriveTestResult.email:''}`
                        : `✕ ${gdriveTestResult.error}`}
                    </div>
                  )}
                  {gdriveFiles.length > 0 && (
                    <div style={{marginTop:'12px'}}>
                      <div style={{fontSize:'11px',fontWeight:'700',color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.8px',marginBottom:'8px'}}>Recent Drive Files</div>
                      {gdriveFiles.slice(0,5).map(f=>(
                        <div key={f.id} style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid var(--border)',fontSize:'12px'}}>
                          <a href={f.webViewLink} target="_blank" rel="noreferrer" style={{color:'var(--signal)',textDecoration:'none',flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{f.name}</a>
                          <span style={{color:'var(--text-muted)',marginLeft:'12px',flexShrink:0}}>{f.size ? Math.round(f.size/1024/1024)+'MB' : '—'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {/* ── BACKUP STREAM ── */}
        {activeTab==='backup'&&(
          <>
            <div style={{background:'rgba(0,230,118,0.05)',border:'1px solid rgba(0,230,118,0.15)',borderRadius:'10px',padding:'12px 16px',marginBottom:'16px',fontSize:'13px',color:'var(--text-muted)'}}>
              📡 Configure a backup source that auto-starts if the main stream drops. Use a pre-recorded file, a second OBS instance, or another RTMP URL.
            </div>
            <div style={{display:'flex',justifyContent:'flex-end',marginBottom:'12px'}}>
              <button style={s.addBtn} onClick={()=>{setBackupForm({name:'',type:'rtmp',source:'',trigger_on_drop:true,trigger_delay_secs:10});setError('');setBackupModal('create');}}>+ Add Backup</button>
            </div>
            {backupStreams.length===0?(
              <div style={{textAlign:'center',padding:'40px',color:'var(--text-muted)',border:'1px dashed var(--border)',borderRadius:'var(--radius-lg)',background:'var(--surface)'}}>
                <div style={{fontSize:'28px',marginBottom:'8px'}}>📡</div>
                <div style={{fontWeight:'600',color:'var(--text)',marginBottom:'4px'}}>No backup streams configured</div>
                Add a pre-recorded file or secondary RTMP source as a fallback
              </div>
            ):(
              backupStreams.map(bs=>(
                <div key={bs.id} style={{...s.card,display:'flex',alignItems:'flex-start',gap:'14px',padding:'14px 18px',opacity:bs.enabled?1:0.6}}>
                  <div style={{fontSize:'24px'}}>📡</div>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:'600',fontSize:'14px',marginBottom:'4px'}}>{bs.name}</div>
                    <div style={{fontSize:'12px',color:'var(--text-muted)',marginBottom:'6px'}}>
                      Type: <strong>{bs.type}</strong> · Trigger delay: <strong>{bs.trigger_delay_secs}s</strong>
                      {bs.trigger_on_drop&&' · Auto-starts on drop'}
                    </div>
                    <div style={{fontFamily:'var(--mono)',fontSize:'11px',color:'var(--signal)',background:'var(--surface2)',padding:'6px 10px',borderRadius:'6px',wordBreak:'break-all'}}>{bs.source}</div>
                  </div>
                  <div style={{display:'flex',gap:'6px',flexDirection:'column',alignItems:'flex-end'}}>
                    <span style={s.badge(bs.enabled)}>{bs.enabled?'Enabled':'Disabled'}</span>
                    <div style={{display:'flex',gap:'5px',marginTop:'4px'}}>
                      <button style={s.editBtn} onClick={()=>{setBackupForm({name:bs.name,type:bs.type,source:bs.source,trigger_on_drop:bs.trigger_on_drop,trigger_delay_secs:bs.trigger_delay_secs});setError('');setBackupModal(bs);}}>Edit</button>
                      <button style={s.delBtn} onClick={async()=>{if(!window.confirm('Remove backup stream?')) return; await api.delete(`/backup-streams/${bs.id}`); loadBackup();}}>Remove</button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </>
        )}

        {/* ── VOD & FALLBACK ── */}
        {activeTab==='vod'&&(
          <>
            {/* Radio Mode */}
            <div style={s.card}>
              <div style={s.cardTitle}>
                <span>📻 Radio Mode</span>
                <span style={{fontSize:'11px',fontWeight:'700',padding:'3px 8px',borderRadius:'20px',
                  background:radioStatus.active&&radioStatus.enabled?'rgba(232,92,53,0.15)':'var(--surface2)',
                  color:radioStatus.active&&radioStatus.enabled?'var(--signal)':'var(--text-muted)',
                  border:`1px solid ${radioStatus.active&&radioStatus.enabled?'var(--signal)':'var(--border)'}`
                }}>{radioStatus.active&&radioStatus.enabled?'● ON AIR':'○ Off'}</span>
              </div>
              <div style={{fontSize:'13px',color:'var(--text-muted)',marginBottom:'14px'}}>
                Live FM radio audio + looping transition videos. When enabled, replaces VOD fallback. DJ going live overrides both.
              </div>
              <div style={s.field}>
                <Toggle value={radioStatus.enabled} onChange={async v=>{
                  await api.patch('/settings',{radio_mode_enabled:v?'true':'false'});
                  setRadioStatus(r=>({...r,enabled:v}));
                }} label="Enable Radio Mode (replaces VOD fallback)"/>
              </div>
              {radioStatus.enabled&&(
                <>
                  <div style={s.field}>
                    <label style={s.label}>FM Radio Stream URL</label>
                    <input value={radioStatus.radioUrl||''} 
                      onChange={e=>setRadioStatus(r=>({...r,radioUrl:e.target.value}))}
                      onBlur={()=>api.patch('/settings',{radio_stream_url:radioStatus.radioUrl}).catch(()=>{})}
                      placeholder="https://dfm.arnait.com/listen/dfm/dfmlive.mp3"/>
                    <div style={s.hint}>Your FM radio stream URL — MP3 or AAC stream. Change this if the primary URL goes down.</div>
                  </div>
                  <div style={s.field}>
                    <Toggle value={radioStatus.muteVideo} onChange={async v=>{
                      await api.patch('/settings',{radio_mute_video:v?'true':'false'});
                      setRadioStatus(r=>({...r,muteVideo:v}));
                    }} label="Mute loop video audio (use only FM radio audio)"/>
                  </div>
                  <div style={s.field}>
                    <label style={s.label}>Loop Videos ({radioStatus.videos?.length||0} uploaded)</label>
                    <div style={{fontSize:'12px',color:'var(--text-muted)',marginBottom:'8px'}}>
                      Upload MP4 videos to loop while FM audio plays. Leave empty for black screen with audio only.
                    </div>
                    <label style={{padding:'8px 14px',background:'var(--signal)',color:'#fff',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontWeight:'600',display:'inline-block'}}>
                      📁 Upload Loop Video
                      <input type="file" accept="video/mp4,video/*" style={{display:'none'}} onChange={async(e)=>{
                        const file = e.target.files[0];
                        if (!file) return;
                        setRadioUploading(true);
                        const form = new FormData();
                        form.append('file', file);
                        try {
                          const {data} = await api.post('/vod/radio-loop', form, {headers:{'Content-Type':'multipart/form-data'}});
                          const {data:rs} = await api.get('/vod/radio-status');
                          setRadioStatus(rs);
                        } catch(e) { alert('Upload failed: ' + e.message); }
                        setRadioUploading(false);
                        e.target.value = '';
                      }}/>
                    </label>
                    {radioUploading && <span style={{marginLeft:'10px',fontSize:'12px',color:'var(--text-muted)'}}>Uploading…</span>}
                  </div>
                  {radioStatus.videos?.length > 0 && (
                    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:'10px',marginTop:'10px'}}>
                      {radioStatus.videos.map((v,i) => (
                        <div key={i} style={{background:'var(--surface2)',borderRadius:'10px',overflow:'hidden',border:'1px solid var(--border)',position:'relative'}}>
                          <img 
                            src={`/api/thumbnails/video?path=${encodeURIComponent(v.path||v)}`}
                            alt={v.name||v}
                            style={{width:'100%',aspectRatio:'16/9',objectFit:'cover',display:'block',background:'#000'}}
                            onError={e=>{ e.target.style.display='none'; }}
                          />
                          <div style={{padding:'6px 8px'}}>
                            <div style={{fontSize:'11px',fontWeight:'600',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',color:'var(--text)'}}>{v.name||`Loop Video ${i+1}`}</div>
                            <button style={{...s.delBtn,width:'100%',marginTop:'4px',fontSize:'11px',padding:'3px 0'}} onClick={async()=>{
                              await api.delete('/vod/radio-loop',{data:{file_path:v.path||v}});
                              const {data:rs} = await api.get('/vod/radio-status');
                              setRadioStatus(rs);
                            }}>Remove</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{display:'flex',gap:'8px',marginTop:'12px',flexWrap:'wrap'}}>
                    <button style={s.saveBtn} onClick={async()=>{
                      await api.patch('/settings',{
                        radio_mode_enabled:'true',
                        radio_stream_url:radioStatus.radioUrl,
                        radio_mute_video:radioStatus.muteVideo?'true':'false'
                      });
                      setRadioStatus(r=>({...r,saved:true}));
                      setTimeout(()=>setRadioStatus(r=>({...r,saved:false})),2000);
                    }}>{radioStatus.saved?'✓ Saved!':'Save Settings'}</button>
                    <button style={{...s.saveBtn,background:radioStatus.starting?'#666':'var(--signal)',opacity:radioStatus.starting?0.7:1}} 
                      disabled={radioStatus.starting}
                      onClick={async()=>{
                      setRadioStatus(r=>({...r,starting:true,msg:''}));
                      try {
                        await api.patch('/settings',{radio_mode_enabled:'true',radio_stream_url:radioStatus.radioUrl,radio_mute_video:radioStatus.muteVideo?'true':'false'});
                        const {data} = await api.post('/vod/radio-mode/start');
                        setRadioStatus(r=>({...r,starting:false,msg:'✓ Radio mode started!',msgColor:'var(--signal)'}));
                        setTimeout(async()=>{
                          const {data:rs} = await api.get('/vod/radio-status');
                          setRadioStatus(r=>({...r,...rs,msg:'',starting:false}));
                        },3000);
                      } catch(e) { 
                        setRadioStatus(r=>({...r,starting:false,msg:'✕ Failed: '+e.message,msgColor:'#ef4444'}));
                      }
                    }}>{radioStatus.starting?'⏳ Starting…':'▶ Start Radio Mode'}</button>
                    <button style={{...s.editBtn,opacity:radioStatus.stopping?0.7:1}} 
                      disabled={radioStatus.stopping}
                      onClick={async()=>{
                      setRadioStatus(r=>({...r,stopping:true,msg:''}));
                      try {
                        await api.patch('/settings',{radio_mode_enabled:'false'});
                        const {data} = await api.post('/vod/radio-mode/stop');
                        setRadioStatus(r=>({...r,stopping:false,enabled:false,msg:'✓ Switched to VOD fallback',msgColor:'var(--signal)'}));
                        setTimeout(async()=>{
                          const {data:rs} = await api.get('/vod/radio-status');
                          setRadioStatus(r=>({...r,...rs,msg:'',stopping:false}));
                        },3000);
                      } catch(e) { 
                        setRadioStatus(r=>({...r,stopping:false,msg:'✕ Failed: '+e.message,msgColor:'#ef4444'}));
                      }
                    }}>{radioStatus.stopping?'⏳ Switching…':'⏹ Switch to VOD Fallback'}</button>
                  </div>
                  {radioStatus.msg&&(
                    <div style={{marginTop:'10px',padding:'8px 12px',borderRadius:'8px',fontSize:'13px',fontWeight:'600',
                      background:radioStatus.msgColor==='var(--signal)'?'rgba(0,200,100,0.1)':'rgba(239,68,68,0.1)',
                      color:radioStatus.msgColor,border:`1px solid ${radioStatus.msgColor}`}}>
                      {radioStatus.msg}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Fallback control */}
            <div style={s.card}>
              <div style={s.cardTitle}>
                <span>Fallback Stream</span>
                <span style={{fontSize:'11px',fontWeight:'700',padding:'3px 8px',borderRadius:'20px',
                  background:fallbackStatus.active?'rgba(0,230,118,0.1)':'var(--surface2)',
                  color:fallbackStatus.active?'var(--signal)':'var(--text-muted)',
                  border:`1px solid ${fallbackStatus.active?'rgba(0,230,118,0.25)':'var(--border)'}`
                }}>{fallbackStatus.active?'● LIVE':'○ Standby'}</span>
              </div>
              <div style={{fontSize:'13px',color:'var(--text-muted)',marginBottom:'14px'}}>
                When enabled and no DJ is live, the fallback playlist streams automatically to keep the channel live 24/7.
                The stream key is <span style={{fontFamily:'var(--mono)',color:'var(--signal)'}}>fallback</span> — viewers on <code>/api/abr/live/master.m3u8</code> will see it automatically.
              </div>
              <div style={{display:'flex',gap:'12px',flexWrap:'wrap',alignItems:'center',marginBottom:'14px'}}>
                <Toggle
                  value={fallbackStatus.enabled}
                  onChange={async v=>{
                    await api.patch('/settings',{fallback_enabled:String(v)});
                    setFallbackStatus(s=>({...s,enabled:v}));
                  }}
                  label="Auto-fallback when no DJ is live"
                />
                <button style={s.addBtn} onClick={async()=>{ await api.post('/vod/fallback/start'); loadVod(); }}>
                  ▶ Start Now
                </button>
                <button style={s.editBtn} onClick={async()=>{ await api.post('/vod/fallback/stop'); loadVod(); }}>
                  ■ Stop
                </button>
              </div>
            </div>

            {/* Upload */}
            <div style={s.card}>
              <div style={s.cardTitle}>Upload Video</div>
              <div style={{fontSize:'13px',color:'var(--text-muted)',marginBottom:'12px'}}>
                Upload MP4 files for use in the fallback playlist. Files are stored in <span style={{fontFamily:'var(--mono)'}}>recordings/vod/</span>
              </div>
              <input type="file" accept="video/mp4,video/*" style={{display:'none'}} id="vod-upload-input"
                onChange={async e=>{
                  const file = e.target.files[0];
                  if(!file) return;
                  setVodUploading(true);
                  setVodUploadProgress(`Uploading ${file.name} (${(file.size/1024/1024/1024).toFixed(1)}GB)…`);
                  try{
                    // Use XMLHttpRequest to support large files and progress
                    await new Promise((resolve, reject) => {
                      const xhr = new XMLHttpRequest();
                      xhr.open('POST', '/api/vod/upload');
                      xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('ms_token')}`);
                      xhr.setRequestHeader('x-filename', file.name);
                      xhr.upload.onprogress = (e) => {
                        if(e.lengthComputable) {
                          const pct = Math.round(e.loaded/e.total*100);
                          setVodUploadProgress(`Uploading ${file.name}… ${pct}%`);
                        }
                      };
                      xhr.onload = () => xhr.status < 400 ? resolve() : reject(new Error(`HTTP ${xhr.status}`));
                      xhr.onerror = () => reject(new Error('Network error'));
                      xhr.send(file);
                    });
                    setVodUploadProgress(`✓ ${file.name} uploaded successfully`);
                    loadVod();
                  }catch(e){ setVodUploadProgress(`✕ Upload failed: ${e.message}`); }
                  finally{ setVodUploading(false); e.target.value=''; }
                }}
              />
              <div style={{display:'flex',gap:'10px',alignItems:'center',flexWrap:'wrap'}}>
                <button style={s.addBtn} onClick={()=>document.getElementById('vod-upload-input').click()} disabled={vodUploading}>
                  {vodUploading?'Uploading…':'+ Upload MP4'}
                </button>
                {vodUploadProgress&&<span style={{fontSize:'13px',color:vodUploadProgress.startsWith('✓')?'var(--signal)':'var(--danger)'}}>{vodUploadProgress}</span>}
              </div>
            </div>

            {/* External Sources */}
            <div style={s.card}>
              <div style={s.cardTitle}>External Sources</div>
              <div style={{fontSize:'13px',color:'var(--text-muted)',marginBottom:'12px'}}>
                Add an RTMP stream, HLS/M3U8 URL, or MP4 URL as a fallback source. The system will pull and re-broadcast it automatically.
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'8px',marginBottom:'12px',fontSize:'12px'}}>
                {[
                  {type:'rtmp',icon:'📡',label:'RTMP Pull',desc:'rtmp://server/live/key'},
                  {type:'hls',icon:'📺',label:'HLS/M3U8',desc:'https://stream.../live.m3u8'},
                  {type:'mp4url',icon:'🎬',label:'MP4 URL',desc:'https://cdn.../video.mp4'},
                ].map(s=>(<div key={s.type} style={{background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:'8px',padding:'10px',textAlign:'center'}}>
                  <div style={{fontSize:'20px',marginBottom:'4px'}}>{s.icon}</div>
                  <div style={{fontWeight:'700',fontSize:'12px'}}>{s.label}</div>
                  <div style={{fontSize:'10px',color:'var(--text-muted)',fontFamily:'var(--mono)',marginTop:'2px'}}>{s.desc}</div>
                </div>))}
              </div>
              <button style={s.addBtn} onClick={()=>{setSourceForm({name:'',source_url:'',source_type:'hls'});setSourceError('');setShowSourceModal(true);}}>+ Add External Source</button>
            </div>

            {/* VOD Library */}
            <div style={s.card}>
              <div style={s.cardTitle}>VOD Library ({vodItems.length} files)</div>
              {vodItems.length===0?(
                <div style={{textAlign:'center',padding:'32px',color:'var(--text-muted)',border:'1px dashed var(--border)',borderRadius:'10px'}}>
                  <div style={{fontSize:'28px',marginBottom:'8px'}}>📼</div>
                  No videos yet — upload an MP4 or import a recording
                </div>
              ):(
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:'12px'}}>
                  {vodItems.map(v=>{
                    const inPlaylist = vodPlaylist.some(p=>p.vod_id===v.id);
                    return(
                      <div key={v.id} style={{background:'var(--surface2)',borderRadius:'10px',overflow:'hidden',border:`1px solid ${inPlaylist?'var(--signal)':'var(--border)'}`,position:'relative'}}>
                        {inPlaylist&&<div style={{position:'absolute',top:'6px',right:'6px',background:'var(--signal)',color:'#fff',fontSize:'9px',fontWeight:'800',padding:'2px 6px',borderRadius:'10px',zIndex:1}}>IN PLAYLIST</div>}
                        <div style={{position:'relative',background:'#000',aspectRatio:'16/9'}}>
                          <img
                            src={`/api/thumbnails/video?path=${encodeURIComponent(v.file_path)}`}
                            alt={v.name}
                            style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}}
                            onError={e=>{e.target.parentNode.style.background='#111';e.target.style.display='none';}}
                          />
                          <div style={{position:'absolute',bottom:'4px',right:'6px',background:'rgba(0,0,0,0.7)',color:'#fff',fontSize:'10px',padding:'1px 5px',borderRadius:'4px'}}>
                            {v.duration_secs?`${Math.floor(v.duration_secs/60)}:${String(v.duration_secs%60).padStart(2,'0')}`:'—'}
                          </div>
                        </div>
                        <div style={{padding:'8px'}}>
                          <div style={{fontWeight:'600',fontSize:'12px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',marginBottom:'2px'}}>{v.name}</div>
                          <div style={{fontSize:'11px',color:'var(--text-muted)',marginBottom:'8px'}}>{formatBytes(v.size_bytes)}</div>
                          <div style={{display:'flex',gap:'4px'}}>
                            {inPlaylist
                              ? <span style={{...s.badge(true),fontSize:'10px',flex:1,textAlign:'center'}}>✓ In playlist</span>
                              : <button style={{...s.editBtn,flex:1,fontSize:'10px',padding:'3px 0'}} onClick={async()=>{
                                  await api.post('/vod/playlist',{vod_id:v.id,sort_order:vodPlaylist.length});
                                  loadVod();
                                }}>+ Playlist</button>
                            }
                            <button style={{...s.delBtn,fontSize:'10px',padding:'3px 8px'}} onClick={async()=>{
                              if(!window.confirm(`Delete "${v.name}"?`)) return;
                              await api.delete(`/vod/${v.id}`);
                              loadVod();
                            }}>✕</button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Fallback Playlist */}
            <div style={s.card}>
              <div style={s.cardTitle}>Fallback Playlist ({vodPlaylist.length} items)</div>
              <div style={{fontSize:'13px',color:'var(--text-muted)',marginBottom:'12px'}}>
                These videos play in order on loop when no DJ is live.
              </div>
              {vodPlaylist.length===0?(
                <div style={{textAlign:'center',padding:'24px',color:'var(--text-muted)',border:'1px dashed var(--border)',borderRadius:'10px'}}>
                  Add videos from the library above to build the fallback playlist
                </div>
              ):(
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:'12px'}}>
                  {vodPlaylist.map((item,i)=>(
                    <div key={item.id} style={{background:'var(--surface2)',borderRadius:'10px',overflow:'hidden',border:'1px solid var(--border)',position:'relative'}}>
                      <div style={{position:'absolute',top:'6px',left:'6px',background:'rgba(0,0,0,0.7)',color:'#fff',fontSize:'10px',fontWeight:'800',padding:'2px 6px',borderRadius:'10px',zIndex:1}}>#{i+1}</div>
                      <div style={{position:'relative',background:'#000',aspectRatio:'16/9'}}>
                        <img
                          src={`/api/thumbnails/video?path=${encodeURIComponent(item.file_path)}`}
                          alt={item.name}
                          style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}}
                          onError={e=>{e.target.parentNode.style.background='#111';e.target.style.display='none';}}
                        />
                        <div style={{position:'absolute',bottom:'4px',right:'6px',background:'rgba(0,0,0,0.7)',color:'#fff',fontSize:'10px',padding:'1px 5px',borderRadius:'4px'}}>
                          {item.duration_secs?`${Math.floor(item.duration_secs/60)}:${String(item.duration_secs%60).padStart(2,'0')}`:'—'}
                        </div>
                      </div>
                      <div style={{padding:'8px'}}>
                        <div style={{fontWeight:'600',fontSize:'12px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',marginBottom:'6px'}}>{item.name}</div>
                        <button style={{...s.delBtn,width:'100%',fontSize:'11px',padding:'3px 0'}} onClick={async()=>{
                          await api.delete(`/vod/playlist/${item.id}`);
                          loadVod();
                        }}>Remove from playlist</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Import from recordings */}
            <div style={s.card}>
              <div style={s.cardTitle}>Import from Recordings</div>
              <div style={{fontSize:'13px',color:'var(--text-muted)',marginBottom:'12px'}}>
                Convert an existing recording into a VOD library item for use in the fallback playlist.
              </div>
              <button style={s.addBtn} onClick={async()=>{
                try{
                  const {data} = await api.get('/recordings?limit=200');
                  setImportRecordings(data.recordings || data || []);
                  setShowImportModal(true);
                }catch(e){alert('Failed to load recordings');}
              }}>Browse Recordings</button>
            </div>
          </>
        )}

        {/* ── CDN & ABR ── */}
        {activeTab==='cdn'&&(
          <>
            <div style={s.card}>
              <div style={s.cardTitle}>Adaptive Bitrate (ABR)</div>
              <div style={{fontSize:'13px',color:'var(--text-muted)',marginBottom:'14px'}}>
                SRS automatically transcodes your stream to 720p and 480p variants. The HLS player switches quality automatically based on viewer bandwidth.
              </div>
              <div style={s.field}>
                <Toggle value={abrSettings.abr_enabled==='true'} onChange={v=>setAbrSettings(s=>({...s,abr_enabled:String(v)}))} label="ABR transcoding enabled (720p + 480p variants)"/>
                <div style={s.hint}>Uses ~2 CPU cores per stream for transcoding. Your server has 8 cores.</div>
              </div>
              <div style={s.field}>
                <Toggle value={abrSettings.webrtc_enabled==='true'} onChange={v=>setAbrSettings(s=>({...s,webrtc_enabled:String(v)}))} label="WebRTC playback enabled (sub-second latency in dashboard)"/>
                <div style={s.hint}>Dashboard preview switches between WebRTC and HLS. Website/app always uses HLS.</div>
              </div>
            </div>

            <div style={s.card}>
              <div style={s.cardTitle}>CDN Push</div>
              <div style={{fontSize:'13px',color:'var(--text-muted)',marginBottom:'14px'}}>
                Push HLS segments to a CDN for global distribution. Works with any S3-compatible storage: Cloudflare R2, Bunny CDN, Backblaze B2, AWS S3.
              </div>
              <div style={s.field}>
                <Toggle value={abrSettings.cdn_enabled==='true'} onChange={v=>setAbrSettings(s=>({...s,cdn_enabled:String(v)}))} label="CDN push enabled"/>
              </div>
              <div style={s.field}>
                <label style={s.label}>CDN Provider</label>
                <select value={abrSettings.cdn_provider} onChange={e=>setAbrSettings(s=>({...s,cdn_provider:e.target.value}))}>
                  <option value="none">Not configured</option>
                  <option value="cloudflare-r2">Cloudflare R2</option>
                  <option value="bunny">Bunny CDN Storage</option>
                  <option value="backblaze">Backblaze B2</option>
                  <option value="aws-s3">AWS S3</option>
                  <option value="custom">Custom S3-compatible</option>
                </select>
              </div>
              <div style={s.grid2}>
                <div style={s.field}>
                  <label style={s.label}>Endpoint URL</label>
                  <input value={abrSettings.cdn_endpoint} onChange={e=>setAbrSettings(s=>({...s,cdn_endpoint:e.target.value}))} placeholder="https://your-account.r2.cloudflarestorage.com"/>
                </div>
                <div style={s.field}>
                  <label style={s.label}>Bucket Name</label>
                  <input value={abrSettings.cdn_bucket} onChange={e=>setAbrSettings(s=>({...s,cdn_bucket:e.target.value}))} placeholder="dennery-fm-stream"/>
                </div>
              </div>
              <div style={s.grid2}>
                <div style={s.field}>
                  <label style={s.label}>Access Key ID</label>
                  <input value={abrSettings.cdn_access_key} onChange={e=>setAbrSettings(s=>({...s,cdn_access_key:e.target.value}))} placeholder="Access Key"/>
                </div>
                <div style={s.field}>
                  <label style={s.label}>Secret Key</label>
                  <input type="password" value={abrSettings.cdn_secret_key} onChange={e=>setAbrSettings(s=>({...s,cdn_secret_key:e.target.value}))} placeholder="Secret Key"/>
                </div>
              </div>
              <div style={s.grid2}>
                <div style={s.field}>
                  <label style={s.label}>Region</label>
                  <input value={abrSettings.cdn_region} onChange={e=>setAbrSettings(s=>({...s,cdn_region:e.target.value}))} placeholder="auto"/>
                </div>
                <div style={s.field}>
                  <label style={s.label}>Public CDN URL</label>
                  <input value={abrSettings.cdn_public_url} onChange={e=>setAbrSettings(s=>({...s,cdn_public_url:e.target.value}))} placeholder="https://cdn.denneryfm.com"/>
                  <div style={s.hint}>Viewers will load HLS from this URL instead of your server</div>
                </div>
              </div>

              <div style={{display:'flex',gap:'10px',flexWrap:'wrap'}}>
                <button style={s.saveBtn} onClick={async()=>{
                  setAbrSaving(true);
                  try{ await api.patch('/abr/settings',abrSettings); setAbrSaved(true); setTimeout(()=>setAbrSaved(false),2000); }
                  catch{}finally{setAbrSaving(false);}
                }} disabled={abrSaving}>{abrSaving?'Saving…':'Save settings'}</button>
                {abrSaved&&<div style={s.savedBadge}>✓ Saved</div>}
                <button style={s.editBtn} onClick={async()=>{
                  setTestingCdn(true); setCdnTestResult(null);
                  try{ const {data}=await api.post('/abr/test-cdn'); setCdnTestResult(data); }
                  catch(e){ setCdnTestResult({success:false,error:e.response?.data?.error||'Failed'}); }
                  finally{setTestingCdn(false);}
                }} disabled={testingCdn}>{testingCdn?'Testing…':'Test CDN'}</button>
              </div>
              {cdnTestResult&&(
                <div style={{marginTop:'12px',...(cdnTestResult.success?s.successBox:s.errorBox)}}>
                  {cdnTestResult.success?`✓ Connected to R2${cdnTestResult.testUrl?' — test file uploaded':''}${cdnTestResult.testUrl?' — '+cdnTestResult.testUrl.split('/').slice(0,4).join('/'):''}`:cdnTestResult.error?`✕ ${cdnTestResult.error}`:'✕ Failed'}
                </div>
              )}
            </div>

            <div style={s.card}>
              <div style={s.cardTitle}>Embed Code</div>
              <div style={{fontSize:'13px',color:'var(--text-muted)',marginBottom:'12px'}}>Copy this to embed the live stream on your website</div>
              <div style={{fontFamily:'var(--mono)',fontSize:'12px',background:'var(--surface2)',padding:'12px 14px',borderRadius:'8px',border:'1px solid var(--border)',wordBreak:'break-all',color:'var(--signal)'}}>
                {`<iframe src="https://${window.location.host}/live" width="100%" height="500" frameborder="0" allowfullscreen></iframe>`}
              </div>
              <button style={{...s.editBtn,marginTop:'8px'}} onClick={()=>navigator.clipboard.writeText(`<iframe src="https://${window.location.host}/live" width="100%" height="500" frameborder="0" allowfullscreen></iframe>`)}>
                Copy embed code
              </button>
            </div>
          </>
        )}

        {/* ── API KEYS ── */}
        {activeTab==='api-keys'&&(
          <>
            {newKey&&(
              <div style={{background:'rgba(0,230,118,0.06)',border:'1px solid rgba(0,230,118,0.25)',borderRadius:'var(--radius-lg)',padding:'18px 22px',marginBottom:'16px'}}>
                <div style={{fontWeight:'700',color:'var(--signal)',marginBottom:'4px'}}>✓ Key Created — "{newKey.name}"</div>
                <div style={{fontSize:'13px',color:'var(--text-muted)',marginBottom:'10px'}}>⚠️ Copy now — it will never be shown again</div>
                <div style={{fontFamily:'var(--mono)',fontSize:'12px',background:'var(--surface2)',padding:'10px 14px',borderRadius:'7px',marginBottom:'8px',wordBreak:'break-all'}}>{newKey.key}</div>
                <button style={s.saveBtn} onClick={()=>{navigator.clipboard.writeText(newKey.key);setKeyCopied(true);setTimeout(()=>setKeyCopied(false),2000);}}>{keyCopied?'✓ Copied!':'Copy Key'}</button>
                <button style={{...s.editBtn,marginLeft:'8px'}} onClick={()=>setNewKey(null)}>Dismiss</button>
              </div>
            )}
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'12px'}}>
              <div style={{fontSize:'14px',color:'var(--text-muted)'}}>{apiKeys.length} key{apiKeys.length!==1?'s':''}</div>
              <button style={s.addBtn} onClick={()=>{setKeyForm({name:'',scopes:['read'],rate_limit_per_min:60,expires_at:''});setError('');setKeyModal(true);}}>+ New Key</button>
            </div>
            {apiKeys.length===0&&!newKey?(
              <div style={{textAlign:'center',padding:'40px',color:'var(--text-muted)',border:'1px dashed var(--border)',borderRadius:'var(--radius-lg)',background:'var(--surface)'}}>
                <div style={{fontSize:'28px',marginBottom:'8px'}}>🔐</div>
                <div style={{fontWeight:'600',color:'var(--text)',marginBottom:'4px'}}>No API keys yet</div>
                Connect your website or app to the Dennery FM platform
              </div>
            ):(
              apiKeys.map(key=>(
                <div key={key.id} style={{...s.card,display:'flex',alignItems:'center',gap:'12px',opacity:key.enabled?1:0.6,padding:'12px 16px'}}>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:'600',fontSize:'14px',marginBottom:'3px'}}>{key.name}</div>
                    <div style={{display:'flex',gap:'8px',flexWrap:'wrap',fontSize:'12px',color:'var(--text-muted)'}}>
                      <span style={s.mono}>{key.key_prefix}…</span>
                      {key.scopes.map(sc=><span key={sc} style={s.scopeBadge(sc)}>{sc}</span>)}
                      <span>{key.rate_limit_per_min}/min</span>
                      {key.last_used_at&&<span>Used {new Date(key.last_used_at).toLocaleDateString()}</span>}
                    </div>
                  </div>
                  <button style={s.badge(key.enabled)} onClick={()=>toggleKey(key)}>{key.enabled?'Enabled':'Disabled'}</button>
                  <button style={s.delBtn} onClick={()=>deleteKey(key)}>Delete</button>
                </div>
              ))
            )}
            <div style={{...s.card,marginTop:'16px'}}>
              <div style={{fontWeight:'700',fontSize:'14px',marginBottom:'4px'}}>📖 API Reference</div>
              <div style={{fontSize:'12px',color:'var(--text-muted)',marginBottom:'12px'}}>
                Base URL: <span style={{fontFamily:'var(--mono)',color:'var(--signal)'}}>{`https://${window.location.host}/api`}</span><br/>
                Auth header: <span style={{fontFamily:'var(--mono)'}}>Authorization: Bearer YOUR_KEY</span>
              </div>
              {[
                ['AUTH',''],
                ['POST','/auth/login','Login — returns JWT token'],
                ['GET','/auth/me','Current user info'],
                ['POST','/auth/change-password','Change password'],
                ['STREAM',''],
                ['GET','/stream/status','Live status, uptime, sessionId, destinations'],
                ['GET','/stream/key','Your RTMP stream key'],
                ['GET','/stream/history','Past stream sessions'],
                ['GET','/stream/destinations','Your destinations'],
                ['POST','/stream/destinations','Add destination'],
                ['PATCH','/stream/destinations/:id','Update destination'],
                ['DELETE','/stream/destinations/:id','Remove destination'],
                ['GET','/stream/global-destinations','Shared destinations'],
                ['DJ MIXER',''],
                ['GET','/handoff/status','Connected DJs, relay status'],
                ['POST','/handoff/activate','Activate handoff for DJ'],
                ['POST','/handoff/switch','Switch active DJ'],
                ['POST','/handoff/stop','Stop relay → fallback'],
                ['RECORDINGS',''],
                ['GET','/recordings','List recordings (page, limit, sort, filter)'],
                ['PATCH','/recordings/:id','Rename recording'],
                ['DELETE','/recordings/:id','Delete recording'],
                ['DELETE','/recordings/bulk','Bulk delete'],
                ['GET','/recordings/:id/download','Download recording'],
                ['POST','/gdrive/upload/:id','Upload to Google Drive'],
                ['HEALTH & VIEWERS',''],
                ['GET','/stream-health/current','Live bitrate, fps, resolution, viewers'],
                ['GET','/stream-health/history','Bitrate history for session'],
                ['GET','/viewers/live','Live viewer IPs, quality, client type'],
                ['VOD & FALLBACK',''],
                ['GET','/vod','VOD library'],
                ['GET','/vod/playlist','Fallback playlist'],
                ['GET','/vod/fallback/status','Fallback running/stopped'],
                ['POST','/vod/fallback/start','Start fallback manually'],
                ['POST','/vod/fallback/stop','Stop fallback'],
                ['POST','/vod/import-recording/:id','Import recording to VOD'],
                ['GUEST STUDIO',''],
                ['GET','/guest/rooms','Active guest rooms'],
                ['POST','/guest/rooms','Create guest room'],
                ['DELETE','/guest/rooms/:roomId','Close guest room'],
                ['GET','/guest/room/:roomId','Room info (no auth required)'],
                ['SECURITY',''],
                ['GET','/security/banned','Banned IPs with geo info'],
                ['DELETE','/security/banned/:ip','Unban IP'],
                ['SETTINGS',''],
                ['GET','/settings','All settings'],
                ['PATCH','/settings','Update settings'],
                ['GET','/settings/logo','Station logo (public, no auth)'],
                ['USERS (Admin)',''],
                ['GET','/auth/users','List all users'],
                ['POST','/auth/users','Create user'],
                ['PATCH','/auth/users/:id','Update user role/name'],
                ['DELETE','/auth/users/:id','Delete user'],
                ['PUBLIC (No auth)',''],
                ['GET','/public/live','Public live status + viewer count'],
                ['GET','/settings/logo','Station logo'],
              ].map(([m,p,d],i)=>{
                const isHeader = !p;
                if (isHeader) return (
                  <div key={i} style={{fontSize:'10px',fontWeight:'800',color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'1px',padding:'10px 0 4px',marginTop:'4px',borderTop:'1px solid var(--border)'}}>
                    {m}
                  </div>
                );
                const color = m==='GET'?'#22c55e':m==='POST'?'#3b82f6':m==='PATCH'?'#f59e0b':m==='DELETE'?'#ef4444':'var(--signal)';
                return (
                  <div key={i} style={{display:'flex',gap:'8px',alignItems:'center',padding:'5px 8px',background:'var(--surface2)',borderRadius:'6px',marginBottom:'3px'}}>
                    <span style={{color,fontWeight:'800',fontSize:'10px',width:'44px',fontFamily:'var(--mono)',flexShrink:0}}>{m}</span>
                    <span style={{fontFamily:'var(--mono)',fontSize:'11px',color:'var(--text)',flex:1}}>{p}</span>
                    <span style={{color:'var(--text-muted)',fontSize:'11px',flexShrink:0}}>{d}</span>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* ── REPORTS ── */}
        {activeTab==='reports'&&isManager&&(
          <>
            <div style={{display:'flex',gap:'6px',marginBottom:'16px'}}>
              {[7,30,90].map(d=><button key={d} style={{...s.editBtn,background:usageDays===d?'var(--signal)':'var(--surface2)',color:usageDays===d?'#000':'var(--text-muted)',border:`1px solid ${usageDays===d?'var(--signal)':'var(--border)'}`}} onClick={()=>setUsageDays(d)}>{d}d</button>)}
              <button style={s.editBtn} onClick={()=>{const token=localStorage.getItem('ms_token');const a=document.createElement('a');a.href=`/api/usage/export?days=${usageDays}&token=${token}`;a.download=`usage-${usageDays}d.csv`;a.click();}}>↓ CSV</button>
            </div>
            {usage&&<>
              <div style={s.statGrid}>
                <div style={s.statCard()}><div style={s.statLabel}>Total Streams</div><div style={s.statValue()}>{usage.totals?.total_streams||0}</div><div style={s.statSub}>{usage.totals?.active_users||0} DJs</div></div>
                <div style={s.statCard()}><div style={s.statLabel}>Stream Time</div><div style={s.statValue()}>{formatSecs(usage.totals?.total_stream_secs)}</div></div>
                <div style={s.statCard()}><div style={s.statLabel}>Recordings</div><div style={s.statValue()}>{usage.totals?.total_recordings||0}</div><div style={s.statSub}>{formatBytes(usage.totals?.total_recording_bytes)}</div></div>
              </div>
              <div style={s.card}>
                <div style={s.cardTitle}>Per DJ</div>
                <table style={s.table}>
                  <thead><tr><th style={s.th}>DJ</th><th style={s.th}>Streams</th><th style={s.th}>Time</th><th style={s.th}>Recordings</th><th style={s.th}>Storage</th><th style={s.th}>Last Stream</th></tr></thead>
                  <tbody>{(usage.userUsage||[]).map(u=>(
                    <tr key={u.id}>
                      <td style={s.td}><div style={{fontWeight:'600'}}>{u.name}</div><div style={{fontSize:'11px',color:'var(--text-muted)'}}>{u.email}</div></td>
                      <td style={s.td}>{u.stream_count||0}</td>
                      <td style={s.td}>
                        {formatSecs(u.total_stream_secs)}
                        <div style={s.barWrap}><div style={s.bar((u.total_stream_secs||0)/maxStreamSecs*100)}/></div>
                      </td>
                      <td style={s.td}>{u.recording_count||0}</td>
                      <td style={s.td}>{formatBytes(u.recording_bytes)}</td>
                      <td style={s.td}>{u.last_stream_at?new Date(u.last_stream_at).toLocaleDateString():'—'}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
              {usage.platformStats?.length>0&&(
                <div style={s.card}>
                  <div style={s.cardTitle}>Platform Breakdown</div>
                  {usage.platformStats.map((p,i)=>(
                    <div key={i} style={{display:'flex',alignItems:'center',gap:'12px',padding:'9px 0',borderBottom:'1px solid var(--border)'}}>
                      <div style={{width:'120px',fontSize:'13px',fontWeight:'600',textTransform:'capitalize'}}>{p.label||p.platform}</div>
                      <div style={{flex:1}}><div style={s.barWrap}><div style={s.bar(p.stream_count/Math.max(...usage.platformStats.map(x=>x.stream_count),1)*100)}/></div></div>
                      <div style={{fontSize:'12px',color:'var(--text-muted)',display:'flex',gap:'12px'}}>
                        <span style={{color:'var(--signal)'}}>{p.success_count} ok</span>
                        {p.error_count>0&&<span style={{color:'var(--danger)'}}>{p.error_count} err</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>}
          </>
        )}

        {/* ── ACTIVITY ── */}
        {activeTab==='activity'&&isManager&&(
          <>
            <div style={{display:'flex',gap:'6px',flexWrap:'wrap',marginBottom:'16px'}}>
              {['','stream','user','destination','recording','settings','apikey'].map(f=>(
                <button key={f} style={{...s.editBtn,background:logFilter===f?'var(--signal)':'var(--surface2)',color:logFilter===f?'#000':'var(--text-muted)'}} onClick={()=>{setLogFilter(f);setLogOffset(0);}}>
                  {f||'All'}
                </button>
              ))}
            </div>
            {logs.length===0?<div style={{textAlign:'center',padding:'40px',color:'var(--text-muted)'}}>No activity yet</div>:
              logs.map(log=>{
                const meta=ACTION_META[log.action]||{icon:'•',color:'var(--text-muted)',label:log.action};
                const detail=log.detail?(typeof log.detail==='object'?Object.entries(log.detail).map(([k,v])=>`${k}: ${v}`).join(' · '):log.detail):'';
                return(
                  <div key={log.id} style={s.logRow}>
                    <div style={s.logIcon(meta.color)}>{meta.icon}</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:'13px',fontWeight:'600',color:meta.color}}>{meta.label}</div>
                      <div style={{fontSize:'12px',color:'var(--text-muted)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',marginTop:'1px'}}>
                        {log.user_name&&<strong>{log.user_name}</strong>}{detail&&` · ${detail}`}
                      </div>
                    </div>
                    <div style={{fontSize:'11px',color:'var(--text-muted)',flexShrink:0,textAlign:'right'}}>
                      <div>{new Date(log.created_at).toLocaleDateString()}</div>
                      <div>{new Date(log.created_at).toLocaleTimeString()}</div>
                    </div>
                  </div>
                );
              })
            }
            {logTotal>50&&(
              <div style={{display:'flex',gap:'8px',justifyContent:'center',marginTop:'16px'}}>
                <button style={s.editBtn} disabled={logOffset===0} onClick={()=>setLogOffset(Math.max(0,logOffset-50))}>← Prev</button>
                <span style={{padding:'6px 12px',fontSize:'13px',color:'var(--text-muted)'}}>{logOffset+1}–{Math.min(logOffset+50,logTotal)} of {logTotal}</span>
                <button style={s.editBtn} disabled={logOffset+50>=logTotal} onClick={()=>setLogOffset(logOffset+50)}>Next →</button>
              </div>
            )}
          </>
        )}

        {/* ── ACCOUNT ── */}
        {activeTab==='account'&&(
          <>
          <div style={s.card}>
            <div style={s.cardTitle}>My Account</div>
            <div style={{padding:'14px 16px',background:'var(--surface2)',borderRadius:'9px',border:'1px solid var(--border)',marginBottom:'20px'}}>
              <div style={{fontWeight:'700',fontSize:'16px',marginBottom:'3px'}}>{user?.name}</div>
              <div style={{color:'var(--text-muted)',fontSize:'13px'}}>{user?.email}</div>
            </div>
            <div style={{fontWeight:'700',fontSize:'14px',marginBottom:'14px'}}>Change Password</div>
            {pwError&&<div style={s.errorBox}>{pwError}</div>}
            {pwSuccess&&<div style={s.successBox}>✓ {pwSuccess}</div>}
            <div style={s.field}><label style={s.label}>Current Password</label><input type="password" value={pwForm.currentPassword} onChange={e=>setPwForm({...pwForm,currentPassword:e.target.value})} placeholder="••••••••"/></div>
            <div style={s.field}><label style={s.label}>New Password</label><input type="password" value={pwForm.newPassword} onChange={e=>setPwForm({...pwForm,newPassword:e.target.value})} placeholder="At least 8 characters"/></div>
            <div style={s.field}><label style={s.label}>Confirm</label><input type="password" value={pwForm.confirm} onChange={e=>setPwForm({...pwForm,confirm:e.target.value})} placeholder="••••••••"/></div>
            <button style={s.saveBtn} onClick={handleChangePassword}>Change password</button>
          </div>

          <div style={s.card}>
            <div style={s.cardTitle}>Two-Factor Authentication</div>
            <div style={{fontSize:'13px',color:'var(--text-muted)',marginBottom:'16px'}}>
              {twoFa.enabled ? '2FA is enabled on your account.' : 'Add an extra layer of security with an authenticator app (Google Authenticator, Authy, 1Password).'}
            </div>
            {twoFa.error&&<div style={s.errorBox}>{twoFa.error}</div>}
            {twoFa.success&&<div style={s.successBox}>{twoFa.success}</div>}
            {!twoFa.enabled && twoFa.step==='idle' && (
              <button style={s.saveBtn} onClick={async()=>{
                try{const{data}=await api.post('/auth/2fa/setup');setTwoFa(t=>({...t,qrCode:data.qrCode,secret:data.secret,step:'scan',error:''}));}
                catch(e){setTwoFa(t=>({...t,error:e.response?.data?.error||'Error'}));}
              }}>Enable 2FA</button>
            )}
            {!twoFa.enabled && twoFa.step==='scan' && (
              <div>
                <p style={{fontSize:'13px',marginBottom:'12px'}}>Scan this QR code with your authenticator app:</p>
                <img src={twoFa.qrCode} alt="QR" style={{width:'180px',height:'180px',borderRadius:'8px',display:'block',marginBottom:'12px',border:'1px solid var(--border)'}}/>
                <p style={{fontSize:'12px',color:'var(--text-muted)',marginBottom:'16px'}}>Manual entry: <code style={{background:'var(--surface2)',padding:'2px 6px',borderRadius:'4px'}}>{twoFa.secret}</code></p>
                <p style={{fontSize:'13px',marginBottom:'8px'}}>Enter the 6-digit code to confirm:</p>
                <div style={{display:'flex',gap:'8px',alignItems:'center',flexWrap:'wrap'}}>
                  <input type="text" inputMode="numeric" maxLength={6} placeholder="000000"
                    value={twoFa.code} onChange={e=>setTwoFa(t=>({...t,code:e.target.value.replace(/[^0-9]/g,'')}))}
                    style={{maxWidth:'140px',textAlign:'center',fontSize:'18px',letterSpacing:'6px'}}/>
                  <button style={s.saveBtn} disabled={twoFa.code.length!==6}
                    onClick={async()=>{
                      try{await api.post('/auth/2fa/verify',{code:twoFa.code});setTwoFa(t=>({...t,enabled:true,step:'idle',code:'',qrCode:null,secret:null,success:'2FA enabled successfully!',error:''}));}
                      catch(e){setTwoFa(t=>({...t,error:e.response?.data?.error||'Invalid code'}));}
                    }}>Confirm</button>
                  <button style={{...s.saveBtn,background:'var(--surface2)',color:'var(--text)'}}
                    onClick={()=>setTwoFa(t=>({...t,step:'idle',code:'',qrCode:null,secret:null}))}>Cancel</button>
                </div>
              </div>
            )}
            {twoFa.enabled && (
              <div>
                <p style={{fontSize:'13px',marginBottom:'8px'}}>Enter your current 6-digit code to disable:</p>
                <div style={{display:'flex',gap:'8px',alignItems:'center',flexWrap:'wrap'}}>
                  <input type="text" inputMode="numeric" maxLength={6} placeholder="000000"
                    value={twoFa.code} onChange={e=>setTwoFa(t=>({...t,code:e.target.value.replace(/[^0-9]/g,'')}))}
                    style={{maxWidth:'140px',textAlign:'center',fontSize:'18px',letterSpacing:'6px'}}/>
                  <button style={{...s.saveBtn,background:'var(--danger-bg)',color:'var(--danger)',border:'1px solid rgba(220,38,38,0.3)'}}
                    disabled={twoFa.code.length!==6}
                    onClick={async()=>{
                      try{await api.post('/auth/2fa/disable',{code:twoFa.code});setTwoFa(t=>({...t,enabled:false,code:'',success:'2FA disabled.',error:''}));}
                      catch(e){setTwoFa(t=>({...t,error:e.response?.data?.error||'Invalid code'}));}
                    }}>Disable 2FA</button>
                </div>
              </div>
            )}
          </div>
          </>
        )}
      </div>

      {/* ── Import Recording Modal ── */}
      {showImportModal&&(
        <div style={s.overlay} onClick={e=>e.target===e.currentTarget&&setShowImportModal(false)}>
          <div style={{...s.modal,maxWidth:'560px'}}>
            <div style={s.modalTitle}>Import Recording to VOD Library</div>
            {importRecordings.length===0?(
              <div style={{textAlign:'center',padding:'24px',color:'var(--text-muted)'}}>No recordings found</div>
            ):(
              <div style={{maxHeight:'400px',overflowY:'auto',display:'flex',flexDirection:'column',gap:'6px'}}>
                {importRecordings.map(rec=>(
                  <div key={rec.id} style={{display:'flex',alignItems:'center',gap:'10px',padding:'10px 14px',background:'var(--surface2)',borderRadius:'8px',border:'1px solid var(--border)'}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:'600',fontSize:'13px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{rec.filename}</div>
                      <div style={{fontSize:'11px',color:'var(--text-muted)'}}>{rec.duration_secs?`${Math.floor(rec.duration_secs/60)}m`:'—'} · {rec.size_bytes?Math.round(rec.size_bytes/1024/1024)+'MB':'—'}</div>
                    </div>
                    <button style={s.addBtn} onClick={async()=>{
                      const name = rec.filename.replace('.mp4','').replace(/sk_[a-z0-9]+-/,'Show - ');
                      try{
                        await api.post(`/vod/import-recording/${rec.id}`,{name});
                        setShowImportModal(false);
                        loadVod();
                      }catch(e){alert('Import failed: '+e.message);}
                    }}>Import</button>
                  </div>
                ))}
              </div>
            )}
            <button style={{...s.modalCancel,marginTop:'12px'}} onClick={()=>setShowImportModal(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* ── External Source Modal ── */}
      {showSourceModal&&(
        <div style={s.overlay} onClick={e=>e.target===e.currentTarget&&setShowSourceModal(false)}>
          <div style={s.modal}>
            <div style={s.modalTitle}>Add External Source</div>
            {sourceError&&<div style={s.errorBox}>{sourceError}</div>}
            <div style={s.field}>
              <label style={s.label}>Name</label>
              <input value={sourceForm.name} onChange={e=>setSourceForm(f=>({...f,name:e.target.value}))} placeholder="Internet Radio Stream"/>
            </div>
            <div style={s.field}>
              <label style={s.label}>Source Type</label>
              <select value={sourceForm.source_type} onChange={e=>setSourceForm(f=>({...f,source_type:e.target.value}))}>
                <option value="hls">HLS / M3U8 Stream</option>
                <option value="rtmp">RTMP Stream</option>
                <option value="mp4url">MP4 URL</option>
              </select>
            </div>
            <div style={s.field}>
              <label style={s.label}>
                {sourceForm.source_type==='rtmp'?'RTMP URL':sourceForm.source_type==='hls'?'HLS/M3U8 URL':'MP4 URL'}
              </label>
              <input
                value={sourceForm.source_url}
                onChange={e=>setSourceForm(f=>({...f,source_url:e.target.value}))}
                placeholder={
                  sourceForm.source_type==='rtmp'?'rtmp://live.example.com/live/streamkey':
                  sourceForm.source_type==='hls'?'https://stream.example.com/live.m3u8':
                  'https://cdn.example.com/video.mp4'
                }
              />
              <div style={s.hint}>
                {sourceForm.source_type==='rtmp'&&'FFmpeg will pull this RTMP stream and re-broadcast it as the fallback.'}
                {sourceForm.source_type==='hls'&&'FFmpeg will pull this HLS stream and re-broadcast it. Useful for internet radio or TV streams.'}
                {sourceForm.source_type==='mp4url'&&'FFmpeg will download and loop this MP4 file as the fallback.'}
              </div>
            </div>
            <button style={s.modalSave} onClick={async()=>{
              if(!sourceForm.name||!sourceForm.source_url){setSourceError('Name and URL required');return;}
              setSourceError('');
              try{
                await api.post('/vod/add-source', sourceForm);
                setShowSourceModal(false);
                loadVod();
              }catch(e){setSourceError(e.response?.data?.error||'Failed');}
            }}>Add Source</button>
            <button style={s.modalCancel} onClick={()=>setShowSourceModal(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* ── Server Modal ── */}
      {serverModal&&(
        <div style={s.overlay} onClick={e=>e.target===e.currentTarget&&setServerModal(null)}>
          <div style={s.modal}>
            <div style={s.modalTitle}>{serverModal==='create'?'Add Ingest Server':'Edit Server'}</div>
            {error&&<div style={s.errorBox}>{error}</div>}
            <div style={s.field}><label style={s.label}>Name</label><input value={serverForm.name} onChange={e=>setServerForm({...serverForm,name:e.target.value})} placeholder="Backup VPS (Miami)"/></div>
            <div style={s.field}><label style={s.label}>Hostname / IP</label><input value={serverForm.host} onChange={e=>setServerForm({...serverForm,host:e.target.value})} placeholder="backup.denneryfm.com"/></div>
            <div style={s.grid2}>
              <div style={s.field}><label style={s.label}>RTMP Port</label><input type="number" value={serverForm.rtmp_port} onChange={e=>setServerForm({...serverForm,rtmp_port:parseInt(e.target.value)})}/></div>
              <div style={s.field}><label style={s.label}>SRT Port</label><input type="number" value={serverForm.srt_port} onChange={e=>setServerForm({...serverForm,srt_port:parseInt(e.target.value)})}/></div>
            </div>
            <div style={s.field}><label style={s.label}>Region (optional)</label><input value={serverForm.region} onChange={e=>setServerForm({...serverForm,region:e.target.value})} placeholder="Saint Lucia, Backup"/></div>
            <button style={s.modalSave} onClick={saveServer}>{serverModal==='create'?'Add Server':'Save'}</button>
            <button style={s.modalCancel} onClick={()=>setServerModal(null)}>Cancel</button>
          </div>
        </div>
      )}

      {/* ── Backup Stream Modal ── */}
      {backupModal&&(
        <div style={s.overlay} onClick={e=>e.target===e.currentTarget&&setBackupModal(null)}>
          <div style={s.modal}>
            <div style={s.modalTitle}>{backupModal==='create'?'Add Backup Stream':'Edit Backup Stream'}</div>
            {error&&<div style={s.errorBox}>{error}</div>}
            <div style={s.field}><label style={s.label}>Name</label><input value={backupForm.name} onChange={e=>setBackupForm({...backupForm,name:e.target.value})} placeholder="Pre-recorded Fallback"/></div>
            <div style={s.field}>
              <label style={s.label}>Type</label>
              <select value={backupForm.type} onChange={e=>setBackupForm({...backupForm,type:e.target.value})}>
                <option value="rtmp">RTMP URL (second encoder / OBS)</option>
                <option value="file">Pre-recorded file path</option>
                <option value="url">Stream URL</option>
              </select>
            </div>
            <div style={s.field}>
              <label style={s.label}>{backupForm.type==='file'?'File Path':'RTMP / Stream URL'}</label>
              <input value={backupForm.source} onChange={e=>setBackupForm({...backupForm,source:e.target.value})} placeholder={backupForm.type==='file'?'/recordings/backup.mp4':'rtmp://...'}/>
              {backupForm.type==='file'&&<div style={s.hint}>Path to MP4 file inside the recordings volume. It will loop when the main stream drops.</div>}
            </div>
            <div style={s.field}>
              <label style={s.label}>Trigger delay (seconds after drop)</label>
              <input type="number" min="0" max="300" value={backupForm.trigger_delay_secs} onChange={e=>setBackupForm({...backupForm,trigger_delay_secs:parseInt(e.target.value)})} style={{width:'100px'}}/>
            </div>
            <div style={{...s.field,marginBottom:'18px'}}>
              <Toggle value={backupForm.trigger_on_drop} onChange={v=>setBackupForm({...backupForm,trigger_on_drop:v})} label="Auto-trigger when main stream drops"/>
            </div>
            <button style={s.modalSave} onClick={async()=>{
              if(!backupForm.name||!backupForm.source){setError('Name and source required');return;}
              setError('');
              try{
                if(backupModal==='create') await api.post('/backup-streams',backupForm);
                else await api.patch(`/backup-streams/${backupModal.id}`,backupForm);
                setBackupModal(null); loadBackup();
              }catch(e){setError(e.response?.data?.error||'Failed');}
            }}>{backupModal==='create'?'Add Backup':'Save'}</button>
            <button style={s.modalCancel} onClick={()=>setBackupModal(null)}>Cancel</button>
          </div>
        </div>
      )}

      {/* ── API Key Modal ── */}
      {keyModal&&(
        <div style={s.overlay} onClick={e=>e.target===e.currentTarget&&setKeyModal(false)}>
          <div style={s.modal}>
            <div style={s.modalTitle}>Create API Key</div>
            {error&&<div style={s.errorBox}>{error}</div>}
            <div style={s.field}><label style={s.label}>Name</label><input placeholder="Website Integration" value={keyForm.name} onChange={e=>setKeyForm({...keyForm,name:e.target.value})}/></div>
            <div style={s.field}>
              <label style={s.label}>Scopes</label>
              <div style={{display:'flex',gap:'8px'}}>
                {['read','stream',...(isAdmin?['admin']:[])].map(sc=>(
                  <button key={sc} style={{padding:'7px 14px',borderRadius:'7px',cursor:'pointer',fontWeight:'700',fontSize:'12px',border:`1px solid ${keyForm.scopes.includes(sc)?scopeColors[sc]:'var(--border)'}`,background:keyForm.scopes.includes(sc)?`${scopeColors[sc]}15`:'var(--surface2)',color:keyForm.scopes.includes(sc)?scopeColors[sc]:'var(--text-muted)'}} onClick={()=>toggleScope(sc)}>{sc}</button>
                ))}
              </div>
              <div style={s.hint}>read — view · stream — actions · admin — full access</div>
            </div>
            <div style={s.field}><label style={s.label}>Rate limit (req/min)</label><input type="number" value={keyForm.rate_limit_per_min} onChange={e=>setKeyForm({...keyForm,rate_limit_per_min:parseInt(e.target.value)})} style={{width:'100px'}}/></div>
            <div style={s.field}><label style={s.label}>Expires (optional)</label><input type="date" value={keyForm.expires_at} onChange={e=>setKeyForm({...keyForm,expires_at:e.target.value})}/></div>
            <button style={s.modalSave} onClick={createKey}>Create Key</button>
            <button style={s.modalCancel} onClick={()=>setKeyModal(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
    </NavBar>
  );
}
